import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { env } from 'process';
import { useLocation, useNavigate } from 'react-router-dom';
import initSqlJs, { Database } from 'sql.js/dist/sql-wasm';
import sqlWasm from 'sql.js/dist/sql-wasm.wasm';
import { Hints } from 'intro.js-react';
import { useLocales } from 'src/locales';
import { domToPng } from 'modern-screenshot';

import * as Yup from 'yup';

import { isChrome, isFirefox, isSafari, isMobile } from 'react-device-detect';

import {
  Firestore,
  addDoc,
  collection,
  getDocs,
  doc,
  getDoc,
  Timestamp,
  updateDoc,
  setDoc,
  deleteDoc,
} from 'firebase/firestore/lite';

import {
  ref,
  uploadString,
  getDownloadURL,
  FirebaseStorage,
  getStorage,
  uploadBytesResumable,
  uploadBytes,
} from 'firebase/storage';

import { generatePdfWithContent, TextItem, ImageItem } from 'src/utils/functions/generate-pdf-with-text';

import {
  useMediaQuery,
  Button,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Divider,
  Switch,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Stack,
  Backdrop,
  InputLabel,
  FormLabel,
  SelectChangeEvent,
  Checkbox,
  OutlinedInput,
  CircularProgress,
  FormHelperText,
  Menu,
  CircularProgressProps,
  Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { styled, useTheme } from '@mui/material/styles';
import { ColorResult, ChromePicker, SketchPicker, GithubPicker } from 'react-color';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { db } from '../../utils/firebaseConfig';
import ColorDisplay from '../color-display-selector';
import IconDisplay from '../IconDisplay';
import InterfacePreview from '../interface-preview';
import {
  ColorPickerOpenType,
  DisplayList,
  IconPickerOpenType,
  ModuleDataType,
  ModuleSelectionType,
  UserInfo,
  FormErrors,
  ValidationError,
  InterfaceContentTitleProps,
  UserUploadedFile,
  PdfGeneratorProps,
} from '../../../types/interfacePreviewTypes';

import { wippe_cxiv_schwarz } from '../interface-modules/cxiv_schwarz';
import { wippe_cv_schwarz } from '../interface-modules/cv_schwarz';
import { wippe_cii_schwarz_2_s } from '../interface-modules/cii_schwarz_2_s';
import { wippe_cii_rot_l_s } from '../interface-modules/cii_rot_l_s';
import { wippe_cii_schwarz_l } from '../interface-modules/cii_schwarz_l';
import { wippe_cii_schwarz_l_s } from '../interface-modules/cii_schwarz_l_s';
import IconDisplayBig from '../IconDisplayBig';
import ColorDisplayBig from '../color-display-selector-big';
import CountrySelect from '../country-select-input';
import CustomGithubPicker from '../custom-github-picker';

const firbaseStorage = getStorage();

const GlobalContainer = styled(Box)(({ theme }) => ({
  padding: '32px',
  [theme.breakpoints.down('md')]: {
    padding: '17px',
  },
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  overflow: 'visible',
}));

const ContentArea = styled(Box)(({ theme }) => ({
  marginTop: '22px',
  [theme.breakpoints.down('md')]: {
    marginTop: '0px',
  },
  height: '100%',
}));

const InterfaceContentTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'nouppercase',
})<InterfaceContentTitleProps>(({ theme, nouppercase, color }) => ({
  textTransform: nouppercase ? 'none' : 'uppercase',
  color: color || theme.palette.primary.main,
}));

const InterfaceContentSubtitle = styled(Typography)(({ theme }) => ({
  display: 'inline-flex',
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  color: theme.palette.primary.main,
  width: 'max-content',
}));

const InterfaceContentSubtitleGray = styled(Typography)(({ theme }) => ({
  display: 'inline-flex',
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  color: '#919EAB',
  width: 'max-content',
}));

const InterfaceContentBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#F2F3F5',
  padding: '2rem 2rem',
  [theme.breakpoints.down('xs')]: {
    padding: '2rem 1rem',
  },
  minHeight: '32rem',
  borderRadius: '1rem',
}));

const BackCharacter = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  transform: 'translateY(0.05rem)',
  paddingRight: '0.25rem',
}));

const GridItem = styled(Grid)(({ theme }) => ({
  minWidth: '10rem',
  height: 'unset',
  textAlign: 'center',
  paddingBottom: '1.5rem',
  color: theme.palette.primary.main,
  [theme.breakpoints.down('md')]: {
    minWidth: '9rem',
    height: '9rem',
  },
  '& .MuiPaper-root': {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const ModuleThumbnailImage = styled('img')(({ theme }) => ({
  mixBlendMode: 'unset',
  maxHeight: '5rem',

  [theme.breakpoints.up('md')]: {
    maxHeight: '8.4rem',
  },

  [theme.breakpoints.up('xl')]: {
    maxHeight: '9rem',
  },
}));

const contactFormInputsStyle = {
  flex: 1,
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': {
      borderTop: 'none',
      borderRight: 'none',
      borderLeft: 'none',
      borderColor: '#777771',
    },
  },
};

const InputsGroupsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '1rem',
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const CenteredBox = styled(Box)({
  // height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

const ButtonContainer = styled(Box)({
  marginTop: '1rem',
  display: 'flex',
  justifyContent: 'space-between',
});

const ContentTransition = styled(Box)<{
  direction: 'left' | 'right';
  transitionkey: number;
}>(({ direction, transitionkey }) => ({
  borderRadius: '0.7rem',
  display: 'flex',
  flexDirection: 'column',
  animation:
    transitionkey === -1 ? 'none' : `${direction === 'left' ? 'slideLeft' : 'slideRight'} 0.3s ease-out forwards`,
  overflowX: 'hidden',
  '@keyframes slideLeft': {
    '0%': {
      transform: 'translateX(100%)',
    },
    '100%': {
      transform: 'translateX(0)',
    },
  },
  '@keyframes slideRight': {
    '0%': {
      transform: 'translateX(-100%)',
    },
    '100%': {
      transform: 'translateX(0)',
    },
  },
}));

const defaultModuleData: ModuleDataType = { ...wippe_cv_schwarz };

interface CircularProgressWithLabelProps extends CircularProgressProps {
  value: number;
  t: (key: string) => string;
}

const CircularProgressWithLabel: React.FC<CircularProgressWithLabelProps> = ({ value, t, ...otherProps }) => (
  <Box display="inline-flex" alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: 'center', px: '0.3rem' }}>
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        width: { xs: '2rem', sm: 'inherit' },
      }}
    >
      <CircularProgress
        sx={{ display: { xs: 'none', sm: 'flex' } }}
        variant="determinate"
        value={value}
        {...otherProps}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            width: { xs: '4rem', sm: 'inherit' },
            pr: { xs: 1, sm: 0 },
          }}
          variant="caption"
          component="div"
          color="text.secondary"
        >{`${Math.round(value)}%`}</Typography>
      </Box>
    </Box>
    <Typography
      pl={{ xs: 0, sm: 1 }}
      variant="caption"
      sx={{ maxWidth: { xs: '5rem', sm: 'unset' } }}
      component="div"
      color="text.secondary"
    >
      {t('uploading_configuration')}
    </Typography>
  </Box>
);

// Wandelt eine data:-URL in ein Uint8Array um – ohne fetch().
// fetch() auf data:-URLs wird in älteren Browsern (u. a. ältere Safari) nicht unterstützt.
function dataUrlToBytes(dataUrl: string): Uint8Array | null {
  const parts = dataUrl.split(',');
  if (parts.length < 2) {
    return null;
  }
  const header = parts[0];
  const isBase64 = header.includes('base64');
  const decoded = isBase64 ? atob(parts[1]) : decodeURIComponent(parts[1]);
  const bytes = new Uint8Array(decoded.length);
  for (let i = 0; i < decoded.length; i += 1) {
    bytes[i] = decoded.charCodeAt(i);
  }
  return bytes;
}

function dataUrlToBlob(dataUrl: string): Blob | null {
  const bytes = dataUrlToBytes(dataUrl);
  if (!bytes) {
    return null;
  }
  const mimeMatch = dataUrl.split(',')[0].match(/data:([^;]+)/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  return new Blob([bytes], { type: mime });
}

// Wartet, bis Schriften und alle Bilder im Element tatsächlich geladen sind,
// sonst fehlen sie im Screenshot (v. a. in Safari/älteren Browsern).
async function waitForAssets(element: HTMLElement): Promise<void> {
  try {
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
  } catch (fontError) {
    // Fonts-API nicht verfügbar – ignorieren.
  }
  const images = Array.from(element.querySelectorAll('img'));
  await Promise.all(
    images.map((img) => {
      if (!img.crossOrigin) {
        img.crossOrigin = 'anonymous';
      }
      if (img.complete && img.naturalWidth > 0) {
        return Promise.resolve();
      }
      if (typeof img.decode === 'function') {
        return img.decode().catch(() => undefined);
      }
      return new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
      });
    })
  );
}

export default function MiunskeKonfiguratorStepper(): JSX.Element {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [savedElementEvent, setSavedElementEvent] = useState<React.MouseEvent<HTMLElement> | null>(null);

  const [selectedButtonIndex, setSelectedButton] = useState<number | null>(null);
  const [iconSearchText, setIconSearchText] = useState<string>('');
  const [iconPickerOpen, setIconPickerOpen] = useState<IconPickerOpenType | null>(null);
  const [sqlDatabase, setSqlDatabase] = useState<Database | null>(null);
  const [arrayOfIcons, setArrayOfIcons] = useState<any[][]>([]);
  const [loadingIcons, setLoadingIcons] = useState<Boolean>(true);
  const [selectedModule, setSelectedModule] = useState<ModuleSelectionType | null>(null);
  const [modulePreviewData, setModulePreviewData] = useState<ModuleDataType>({
    ...defaultModuleData,
  });
  const [previousVersionModuleCopy, setPreviousVersionModuleCopy] = useState<ModuleDataType | null>(null);
  const [versionUpdated, setVersionUpdated] = useState<boolean>(false);
  const [colorPickerOpen, setColorPickerOpen] = useState<ColorPickerOpenType | null>(null);
  const [statusLedCurrentColor, setStatusLedCurrentColor] = useState<string>('#FF0000');
  const [displaySelectorOpen, setDisplaySelectorOpen] = useState<boolean>(false);
  const [interfaceBackgroundColorPickerOpen, setInterfaceBackgroundColorPickerOpen] = useState<Boolean>(false);

  const [invalidRalNumber, setInvalidRalNumber] = useState<boolean>(false);
  const [interfaceBorderColorPickerOpen, setInterfaceBorderColorPickerOpen] = useState<Boolean>(false);
  const [direction, setDirection] = useState<'left' | 'right'>('left');
  const [transitionKey, setTransitionKey] = useState<number>(-1);
  const [userInfo, setUserInfo] = useState<UserInfo>(modulePreviewData.userInfo);
  const [errors, setErrors] = useState<FormErrors>({});

  const [sendingModule, setSendingModule] = useState<boolean>(false);
  const [moduleSent, setModuleSent] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [autoSavedProgress, setAutoSavedProgress] = useState<boolean>(false);
  const [dissmissHints, setDissmissHints] = useState<boolean>(false);
  const [resetChangesConfirmationDialogOpen, setResetChangesConfirmationDialogOpen] = useState<boolean>(false);

  const [hintsActive, setHintsActive] = useState<boolean>(false);
  const [configurationUploadProgress, setConfigurationUploadProgress] = useState<number>(-1);
  const [konfiguratorLanguage, setKonfiguratorLanguage] = useState<string | null>(null);
  const [pickerPosition, setPickerPosition] = useState({ left: 0, top: 0 });
  const [ledContainerScrollOffset, setLedContainerScrollOffset] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const interfacePreviewRef = useRef<HTMLDivElement | null>(null);
  const [projectTitleDialogOpen, setProjectTitleDialogOpen] = useState<boolean>(false);
  const [projectTitle, setProjectTitle] = useState<string>('');
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [userDismissedHints, setUserDismissedHints] = useState<boolean>(false);
  // Estado para controlar el diálogo
  const [openConfirmationDialogForUploadCustomSymbol, setOpenConfirmationDialogForUploadCustomSymbol] = useState(false);
  const [userUploadedCustomSymbol, setUserUploadedCustomSymbol] = useState<File | null>(null);
  const [userUploadedCustomSymbolDescription, setUserUploadedCustomSymbolDescription] = useState<string>('');
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallWebScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const customPopover = usePopover();
  const hintPopover = usePopover();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { allLangs, t, currentLang, onChangeLang } = useLocales();
  const [buttonRef, setButtonRef] = useState<HTMLDivElement | null>(null);

  const handleSetButtonRef = useCallback((bref: HTMLDivElement | null) => {
    if (!bref) return;

    setButtonRef(bref);
  }, []);

  const steps = [
    {
      index: 0,
      label: t('step1_label'),
    },
    {
      index: 1,
      label: t('step2_label'),
    },
    {
      index: 2,
      label: t('step4_label'),
    },
  ];

  const moduleList = [
    {
      index: 0,
      id: 'cii_schwarz_l',
      type: 'Contura II',
      name: 'interface_module_1_name',
      image: `${process.env.REACT_APP_BASE_URL}assets/images/interfacesPreviews/cii_schwarz_l.png`,
    },
    {
      index: 1,
      id: 'cii_schwarz_l_s',
      type: 'Contura II',
      name: 'interface_module_2_name',
      image: `${process.env.REACT_APP_BASE_URL}assets/images/interfacesPreviews/cii_schwarz_l_s.png`,
    },
    {
      index: 2,
      id: 'cii_rot_l_s',
      type: 'Contura II',
      name: 'interface_module_3_name',
      image: `${process.env.REACT_APP_BASE_URL}assets/images/interfacesPreviews/cii_rot_l_s.png`,
    },
    {
      index: 3,
      id: 'cii_schwarz_2_s',
      type: 'Contura II',
      name: 'interface_module_4_name',
      image: `${process.env.REACT_APP_BASE_URL}assets/images/interfacesPreviews/cii_schwarz_2_s.png`,
    },

    {
      index: 4,
      id: 'cv_schwarz',
      type: 'Contura V',
      name: 'interface_module_5_name',
      image: `${process.env.REACT_APP_BASE_URL}assets/images/interfacesPreviews/cv_schwarz.png`,
    },

    {
      index: 5,
      id: 'cxiv_schwarz',
      type: 'Contura XIV',
      name: 'interface_module_6_name',
      image: `${process.env.REACT_APP_BASE_URL}assets/images/interfacesPreviews/cxiv_schwarz.png`,
    },
  ];

  const displayList: DisplayList[] = [
    {
      id: 'oled_white',
      name: t('oled_display_white'),
      type: 'oled',
      image: {
        horizontal: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_oled_placeholder_white_horizontal.png`,
        vertical: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_oled_placeholder_white_vertical.png`,
      },
    },
    {
      id: 'oled_yellow',
      name: t('oled_display_yellow'),
      type: 'oled',
      image: {
        horizontal: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_oled_placeholder_yellow_horizontal.png`,
        vertical: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_oled_placeholder_yellow_vertical.png`,
      },
    },
    {
      id: 'oled_blue',
      name: t('oled_display_blue'),
      type: 'oled',
      image: {
        horizontal: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_oled_placeholder_blue_horizontal.png`,
        vertical: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_oled_placeholder_blue_vertical.png`,
      },
    },
    {
      id: 'oled_green',
      name: t('oled_display_green'),
      type: 'oled',
      image: {
        horizontal: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_oled_placeholder_green_horizontal.png`,
        vertical: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_oled_placeholder_green_vertical.png`,
      },
    },
    {
      id: 'tft_landwirtschaft',
      name: t('tft_display_agricultural'),
      type: 'tft',
      image: {
        horizontal: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_tft_placeholder_landwirtschaft_horizontal.png`,
        vertical: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_tft_placeholder_landwirtschaft_vertical.png`,
      },
    },
    {
      id: 'tft_baufahrzeug',
      name: t('tft_display_construction'),
      type: 'tft',
      image: {
        horizontal: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_tft_placeholder_baufahrzeug_horizontal.png`,
        vertical: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_tft_placeholder_baufahrzeug_vertical.png`,
      },
    },
    {
      id: 'tft_feuerwehr',
      name: t('tft_display_rescue'),
      type: 'tft',
      image: {
        horizontal: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_tft_placeholder_feuerwehr_horizontal.png`,
        vertical: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_tft_placeholder_feuerwehr_vertical.png`,
      },
    },
    {
      id: 'tft_kommunalfahrzeug',
      name: t('tft_display_municipal'),
      type: 'tft',
      image: {
        horizontal: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_tft_placeholder_kommunalfahrzeug_horizontal.png`,
        vertical: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_tft_placeholder_kommunalfahrzeug_vertical.png`,
      },
    },
    {
      id: 'tft_rettungswagen',
      name: t('tft_display_ambulance'),
      type: 'tft',
      image: {
        horizontal: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_tft_placeholder_rettungswagen_horizontal.png`,
        vertical: `${process.env.REACT_APP_BASE_URL}assets/images/placeholders/display_tft_placeholder_rettungswagen_vertical.png`,
      },
    },
  ];

  const arrowPositionOfPopover = () => {
    if (isTabletScreen && selectedButtonIndex !== -1) {
      return 'top-left';
    }
    if (isTabletScreen) {
      return 'top-right';
    }
    if (isSmallWebScreen) {
      return 'left-center';
    }
    return 'left-center';
  };

  useEffect(() => {
    let moduleData = null;

    switch (selectedModule?.id) {
      case 'cii_schwarz_l':
        moduleData = wippe_cii_schwarz_l;
        break;
      case 'cxiv_schwarz':
        moduleData = wippe_cxiv_schwarz;
        break;
      case 'cv_schwarz':
        moduleData = wippe_cv_schwarz;
        break;
      case 'cii_schwarz_2_s':
        moduleData = wippe_cii_schwarz_2_s;
        break;
      case 'cii_rot_l_s':
        moduleData = wippe_cii_rot_l_s;
        break;
      case 'cii_schwarz_l_s':
        moduleData = wippe_cii_schwarz_l_s;
        break;
      default:
        console.log('Finished fetching module data.');
        return;
    }

    setModulePreviewData(JSON.parse(JSON.stringify(moduleData)));

    const preloadImages = (image: string | null) => {
      if (image != null) {
        const img = new Image();
        img.src = image;
      }
    };

    preloadImages(moduleData.background.imageUrl);
  }, [selectedModule]);

  const showErrorSnackbar = useCallback(
    (message: string) => {
      enqueueSnackbar(message, {
        variant: 'error',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        autoHideDuration: 5000,
      });
    },
    [enqueueSnackbar]
  );

  const showSuccessSnackbar = useCallback(
    (message: string) => {
      enqueueSnackbar(message, {
        variant: 'success',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        autoHideDuration: 5000,
      });
    },
    [enqueueSnackbar]
  );

  const showWarningSnackbar = useCallback(
    (message: string) => {
      enqueueSnackbar(message, {
        variant: 'warning',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        autoHideDuration: 5000,
      });
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    // If there is a language in the html tag
    if (document.documentElement.lang) {
      const htmlLang = document.documentElement.lang.split('-')[0];
      if (htmlLang !== currentLang.value) {
        onChangeLang(htmlLang);
        setKonfiguratorLanguage(htmlLang);
      } else {
        setKonfiguratorLanguage(currentLang.value);
      }
    } else {
      setKonfiguratorLanguage(currentLang.value);
    }
  }, [currentLang, onChangeLang]);

  const incrementVersion = useCallback((version: string): string => {
    const parts = version.split('.');
    if (parts.length !== 2) {
      throw new Error('Error incrementing version number.');
    }

    const major = parts[0];
    let minor = parseInt(parts[1], 10);
    minor += 1;

    return `${major}.${minor}`;
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const SQL = await initSqlJs({ locateFile: () => sqlWasm });
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}databases/icons_db_new.sqlite`);
        const buffer = await response.arrayBuffer();
        const sqliteDB = new SQL.Database(new Uint8Array(buffer));
        setSqlDatabase(sqliteDB);
        const results = sqliteDB.exec('SELECT * FROM Icons');
        const filteredResults = results[0].values
          .filter((row) => {
            if ((row[10] === null || row[10] === undefined) && process.env.NODE_ENV === 'development') {
              console.log('Icon found with null or undefined data:', row);
            }
            return row[10] !== null && row[10] !== undefined;
          })
          .map((row) => {
            const iconData = row[10];
            if (typeof iconData === 'string') {
              const encoder = new TextEncoder();
              return [...row.slice(0, 10), encoder.encode(iconData), ...row.slice(11)];
            }
            return row;
          });

        setArrayOfIcons((prevState) => [...filteredResults]);
        setLoadingIcons(false);
      } catch (error) {
        console.error('There was an error trying to load the database:', error);
        showErrorSnackbar(t('error_fetching_database'));
      }
    };

    loadData();
  }, [enqueueSnackbar, showErrorSnackbar, t]);

  useEffect(() => {
    if (konfiguratorLanguage === null) {
      return;
    }

    const loadConfig = async () => {
      if (editMode || !configId) return;

      if (autoSavedProgress === true) {
        return;
      }

      if (currentStep === 3) {
        return;
      }

      const autoSavedRef = doc(db, `wippenConfigurations/${configId}/_autoSavedData`, 'default');
      const autoSavedSnap = await getDoc(autoSavedRef);

      if (autoSavedSnap.exists()) {
        console.log(`Auto saved data found for Konfigurator with ID: ${configId}`);
        setEditMode(true);
        const autoSavedData = autoSavedSnap.data();
        setHintsActive(false);
        setCurrentStep(1);

        setModulePreviewData(autoSavedData as ModuleDataType);
        setUserInfo(autoSavedData.userInfo as UserInfo);
        setPreviousVersionModuleCopy(JSON.parse(JSON.stringify(autoSavedData as ModuleDataType)));
        setIsReadOnly(true);
        showSuccessSnackbar(t('auto_saved_data_loaded'));
      } else {
        const docRef = doc(db, 'wippenConfigurations', configId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const configData = docSnap.data();
          setCurrentStep(1);
          setEditMode(true);
          // TODO: PROYECT TITLE
          setProjectTitle(configData?.projectTitle ? configData.projectTitle : 'Untitled Project');
          setModulePreviewData(configData as ModuleDataType);

          setUserInfo(configData?.userInfo as UserInfo);
          setPreviousVersionModuleCopy(JSON.parse(JSON.stringify(configData as ModuleDataType)));
          setIsReadOnly(true);
        } else {
          console.log(`ERROR: Could not find configuration with the ID: ${configId}`);
        }
      }
    };

    const queryParams = new URLSearchParams(location.search);
    const configId = queryParams.get('k_id');

    loadConfig();
  }, [
    location,
    editMode,
    currentStep,
    selectedModule,
    autoSavedProgress,
    t,
    showSuccessSnackbar,
    konfiguratorLanguage,
  ]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Element;

      // Prevents triggering the function when clicking on any area inside the color picker
      if (target.closest('.chrome-picker')) {
        return;
      }

      if (containerRef.current && !containerRef.current.contains(target)) {
        setColorPickerOpen(null);
        setInterfaceBackgroundColorPickerOpen(false);
        setInterfaceBorderColorPickerOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (previousVersionModuleCopy === null) {
      return;
    }

    const currentData = JSON.stringify(modulePreviewData);
    const previousData = JSON.stringify(previousVersionModuleCopy);

    if (versionUpdated === true) {
      return;
    }

    if (currentData !== previousData) {
      if (isReadOnly) {
        return;
      }
      setModulePreviewData((prevData) => ({
        ...prevData,
        version: incrementVersion(prevData.version),
      }));

      setVersionUpdated(true);
    }
  }, [
    modulePreviewData,
    previousVersionModuleCopy,
    setPreviousVersionModuleCopy,
    versionUpdated,
    incrementVersion,
    isReadOnly,
  ]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(`${t('firstname_is_required')}`),
    lastname: Yup.string().required(`${t('lastname_is_required')}`),
    firma: Yup.string().required(`${t('company_is_required')}`),
    postalCode: Yup.string().required(`${t('postal_code_is_required')}`),
    country: Yup.string().required(`${t('country_is_required')}`),
    email: Yup.string()
      .email(`${t('email_must_be_valid')}`)
      .required(`${t('email_is_required')}`),
    telephoneNumber: Yup.string(),
    comments: Yup.string(),
    clientNumber: Yup.string().nullable(),
  });
  function uint8ToBase64(buffer: Uint8Array | null | undefined, data?: any) {
    if (!buffer || !(buffer instanceof Uint8Array)) {
      console.error('Invalid input for uint8ToBase64 at MiunskeKonfigurator/index.tsx:', buffer);
      console.error('Data:', data);
      return '';
    }

    return btoa(String.fromCharCode.apply(null, Array.from(buffer)));
  }

  // The first number of the Uint8Array defines the type of image
  // 137 for PNG, 60 for SVG
  const getMimeType = (iconData: Uint8Array | number[]) => {
    if (!iconData || !(iconData instanceof Uint8Array)) {
      // console.log('ERROR: Invalid input for getMimeType at MiunskeKonfigurator/index.tsx:', iconData);

      return '';
    }
    return iconData[0] === 137 ? 'data:image/png;base64,' : 'data:image/svg+xml;base64,';
  };

  const iconMap = arrayOfIcons?.reduce((acc, iconData) => {
    const iconId = iconData[1];
    const iconUint8Array = iconData[10];
    acc[iconId] = iconUint8Array;
    return acc;
  }, {} as Record<number, Uint8Array>);

  const uploadImageToStorage = async (base64Image: string, storage: FirebaseStorage, imageSubcategory: string) => {
    const match = base64Image.match(/^data:image\/(png|jpeg|jpg);base64,/);
    let extension = 'png';

    if (match && match[1]) {
      extension = match[1] === 'jpeg' ? 'jpeg' : match[1];
    }

    const base64FormattedString = base64Image.replace(/^data:image\/(png|jpeg|jpg);base64,/, '');

    const timestamp = new Date().getTime();
    const imageName = `image_${timestamp}.${extension}`;

    const imageRef = ref(storage, `images/${imageSubcategory}/${imageName}`);

    await uploadString(imageRef, base64FormattedString, 'base64');
    return getDownloadURL(imageRef);
  };

  const determineBackButtonVisibility = (): 'visible' | 'hidden' => {
    if (currentStep === 1 && modulePreviewData.isSubmitted) {
      return 'hidden';
    }
    if (currentStep === 0) {
      return 'hidden';
    }
    return 'visible';
  };

  const disableNextButton = (): boolean => {
    if (sendingModule) {
      return true;
    }

    if (moduleSent && currentStep === 2) {
      return true;
    }

    if (isReadOnly && currentStep === 2) {
      return true;
    }

    if (editMode === false && selectedModule === null) {
      return true;
    }

    return false;
  };

  // useEffect(() => {
  //   console.log(arrayOfIcons)
  // }, [arrayOfIcons]);

  const handleOpenProjectTitleDialog = () => {
    setProjectTitleDialogOpen(true);
  };

  const handleCloseProjectTitleDialog = () => {
    setProjectTitleDialogOpen(false);
  };

  const handleProjectTitleContinue = () => {
    setProjectTitleDialogOpen(false);
    setCurrentStep(1);
  };

  const handleRockerSwitchLightClick = (e: React.MouseEvent<HTMLElement>, buttonIndex: number) => {
    if (hintsActive && buttonIndex === 1 && userDismissedHints === false) {
      hintPopover.onOpen(e);
      setSavedElementEvent(e);
      setSelectedButton(buttonIndex);
      setAnchorEl(e.currentTarget);
      return;
    }

    setSelectedButton(buttonIndex);
    setAnchorEl(e.currentTarget);
    customPopover.onOpen(e);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    customPopover.onClose();
  };

  const handleButtonsChange = (ledId: number, checked?: boolean, color?: string, icon?: number | null) => {
    if (isReadOnly) {
      return;
    }

    setModulePreviewData((prevData) => {
      const newData = { ...prevData };

      if (selectedButtonIndex === null || selectedButtonIndex === undefined) {
        return newData;
      }

      const ledIndex = ledId - 1;

      if (ledId === 0) {
        if (icon !== undefined) {
          newData.elements.symbols[selectedButtonIndex].icon = icon;
        }

        if (color !== undefined) {
          newData.elements.symbols[selectedButtonIndex].color = color;
        }

        return newData;
      }

      return newData;
    });
  };

  const handleChangeButtonEmbossing = (e: any) => {
    if (modulePreviewData != null && selectedButtonIndex !== null) {
      if (selectedButtonIndex === -1) {
        if (isReadOnly) {
          return;
        }
        setModulePreviewData((prevData) => {
          const newData = { ...prevData };
          return newData;
        });
        return;
      }
      if (isReadOnly) {
        return;
      }
      setModulePreviewData((prevData) => {
        const newData = { ...prevData };
        return newData;
      });
    }
  };

  const handleChangeOrientation = (orientation: '0' | '90' | '180' | '270') => {
    if (isReadOnly) {
      return;
    }
    setModulePreviewData((prevData) => {
      const newData = { ...prevData };
      newData.rotation = orientation;
      return newData;
    });
  };

  const downloadImage = (imageX: any, { name = 'img', extension = 'jpg' } = {}) => {
    const a = document.createElement('a');
    a.href = imageX;
    a.download = 'asas';
    a.click();
  };

  async function createThumbnail(base64Image: string, maxWidth: number) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const scaleFactor = maxWidth / img.width;
        canvas.width = maxWidth;
        canvas.height = img.height * scaleFactor;
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = base64Image;
    });
  }

  const handleEnableEditMode = () => {
    /// do successfull snackbar with message that edit mode is enabled
    showSuccessSnackbar(t('edit_mode_enabled_snackbar_message'));

    setIsReadOnly(false);
  };

  const generateWippenPdf = async (screenshotPng: string, moduleData: ModuleDataType): Promise<Blob> => {
    try {
      /* -------- Load base template -------- */
      // WICHTIG: über REACT_APP_BASE_URL laden, nicht über einen absoluten Root-Pfad.
      // Live läuft die App unter miunske.com/wippen-konfigurator/, dort würde
      // '/assets/pdf/...' auf miunske.com/assets/... zeigen und 404 liefern.
      const templateUrl = `${process.env.REACT_APP_BASE_URL}assets/pdf/output_pdf_empty.pdf`;
      const templateRes = await fetch(templateUrl);

      if (!templateRes.ok) {
        throw new Error(`PDF-Vorlage konnte nicht geladen werden (${templateRes.status}): ${templateUrl}`);
      }

      const existingPdfBytes = await templateRes.arrayBuffer();

      // fetch() wirft bei 404 nicht — ohne diese Prüfung würde eine HTML-Fehlerseite
      // als "PDF" weiterverarbeitet und pdf-lib erst später unverständlich scheitern.
      const pdfSignature = new Uint8Array(existingPdfBytes.slice(0, 4));
      const isPdf = String.fromCharCode(...pdfSignature) === '%PDF';
      if (!isPdf) {
        throw new Error(
          `Antwort ist kein PDF (Content-Type: ${templateRes.headers.get('content-type')}): ${templateUrl}`
        );
      }

      /* -------- Calculate text width for centering -------- */
      // Helper function to calculate text width (approximation)
      // This is a simplified version since we don't have direct access to font metrics
      const calculateTextWidth = (text: string, fontSize: number): number => {
        // Approximate width calculation using average character width
        // Adjust this multiplier based on the font
        const avgCharWidth = fontSize * 0.4;
        return text.length * avgCharWidth;
      };

      // Function to get X coordinate for centered text
      const getCenteredXPosition = (text: string, fontSize: number, pageWidth: number): number => {
        const textWidth = calculateTextWidth(text, fontSize);
        return (pageWidth - textWidth) / 2;
      };

      // A4 standard width (595 points)
      const PAGE_WIDTH = 595;

      /* -------- Prepare text layer -------- */
      const dateCreated = new Date().toLocaleDateString('de-DE');
      const userFullName =
        userInfo.name && userInfo.lastname ? `${userInfo.name[0].toUpperCase()}. ${userInfo.lastname}` : '';

      const wippeType = moduleData.module || '';
      const wippeTypeFontSize = 18;

      // Calculate centered X position for wippeType
      const centeredWippeTypeX = getCenteredXPosition(wippeType, wippeTypeFontSize, PAGE_WIDTH);

      const userCompanyName = userInfo.firma;

      const texts: TextItem[] = [
        // Center the wippeType text using the calculated X position
        { text: wippeType, x: centeredWippeTypeX, y: 580, size: wippeTypeFontSize },
        { text: dateCreated, x: 202, y: 43, size: 5 },
        { text: 'Online', x: 245, y: 43, size: 5 },
        { text: dateCreated, x: 320, y: 99.5, size: 5 },
        { text: userFullName || ' - ', x: 353, y: 99.5, size: 5 },
        { text: projectTitle || 'Unbenannt', x: 460, y: 121.5, size: 5 },
        { text: userCompanyName || ' - ', x: 433, y: 111, size: 5 },
        { text: wippeType, x: 397, y: 93, size: 6 },
        { text: 'Symbol-ID:', x: 270, y: 190, size: 6 },

        ...Object.entries(moduleData.elements.symbols)
          .sort(([, a], [, b]) => (a.variant === 'top' ? -1 : b.variant === 'top' ? 1 : 0))
          .map(
            ([, symbol], index): TextItem => ({
              text: symbol.icon ? `• ${symbol.variant === 'top' ? 'Top:' : 'Bottom:'} ID ${symbol.icon}` : '',
              x: 270,
              y: 180 - index * 10,
              size: 6,
            })
          ),
        {
          text: moduleData.userUploadedFiles.length !== 0 ? 'Custom uploaded symbols:' : '',
          x: 400,
          y: 190,
          size: 6,
        },

        ...moduleData.userUploadedFiles.map(
          (file, index): TextItem => ({
            text: file.fileName ? `• ${file.fileName} (ID: ${file.id})` : `unbenannt_symbol (ID: ${file.id})`,

            x: 400,
            y: 180 - index * 10,
            size: 6,
          })
        ),
      ];

      // Convert screenshot from base64 to ArrayBuffer (ohne fetch(data:), für ältere Browser)
      const screenshotByteArray = dataUrlToBytes(screenshotPng);
      if (!screenshotByteArray) {
        throw new Error('Screenshot konnte nicht in Bytes umgewandelt werden.');
      }
      const screenshotBytes = screenshotByteArray.buffer;

      const images: ImageItem[] = [{ bytes: screenshotBytes, format: 'png', x: 270, y: 390, scale: 0.655 }];

      /* -------- Generate PDF -------- */
      const pdfBlob = await generatePdfWithContent({
        existingPdfBytes,
        texts,
        images,
      });

      return pdfBlob;
    } catch (err) {
      console.error('Error generating PDF:', err);
      throw new Error('Failed to generate PDF');
    }
  };

  const uploadPdfToStorage = async (pdfBlob: Blob, storage: FirebaseStorage, moduleId: string): Promise<string> => {
    const timestamp = new Date().getTime();
    const pdfName = `wippe_${moduleId}_${timestamp}.pdf`;
    const storageRef = ref(storage, `pdfs/wippen-pdfs/${pdfName}`);

    const uploadedPdf = await uploadBytes(storageRef, pdfBlob);
    const downloadURL = await getDownloadURL(uploadedPdf.ref);

    return downloadURL;
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setUserInfo({
        name: 'Matias',
        lastname: 'Espina',
        firma: 'Devbite',
        postalCode: '12345',
        country: 'Deutschland',
        email: 'matiasespina1991@gmail.com',
        telephoneNumber: '123456789',
        comments: 'Test',
        clientNumber: '123456',
      });
    }
  }, []);

  const sendModuleToFirebase = async (
    moduleData: ModuleDataType,
    database: Firestore,
    storage: FirebaseStorage,
    usrInfo: UserInfo,
    { isSubmitted = false }: { isSubmitted?: boolean }
  ) => {
    setSendingModule(true);

    setConfigurationUploadProgress(25);

    try {
      if (!moduleData.createdAt) {
        moduleData.createdAt = Timestamp.now();
      }

      moduleData.updatedAt = Timestamp.now();

      moduleData.userInfo = usrInfo;

      const interfaceElement = interfacePreviewRef.current;
      let screenshotPng = '';

      if (interfaceElement) {
        try {
          setConfigurationUploadProgress(20);

          console.log('Creating screenshot...');

          if (process.env.NODE_ENV === 'development') {
            console.log('---------------');
            // check what browser is being used
            if (isFirefox) {
              console.log('Firefox browser detected.');
            }
            if (isChrome) {
              console.log('Chrome browser detected.');
            }
            if (isSafari) {
              console.log('Safari browser detected.');
            }
            if (isMobile) {
              console.log('Mobile device detected.');
            }
            console.log('---------------');
          }

          const screenshotOptions = {
            quality: 0.9,
            backgroundColor: 'transparent',
          };

          await waitForAssets(interfaceElement);

          // Warm-up-Lauf für ALLE Browser: primt den internen Bild-Cache der Lib.
          setConfigurationUploadProgress(35);
          try {
            await domToPng(interfaceElement, screenshotOptions);
          } catch (warmupError) {
            console.warn('Screenshot warm-up run failed (ignored):', warmupError);
          }

          setConfigurationUploadProgress(50);

          // Eigentlicher Lauf.
          screenshotPng = await domToPng(interfaceElement, screenshotOptions);

          setConfigurationUploadProgress(70);

          const blobbed = screenshotPng ? dataUrlToBlob(screenshotPng) : null;

          // Plausibilitätscheck: ein sehr kleines Bild deutet auf einen leeren Screenshot hin.
          if (blobbed && blobbed.size > 1000) {
            const timestamp = new Date().getTime();
            const imageName = `preview_${timestamp}.png`;
            const storageRef = ref(storage, `images/module-preview-screenshots/${imageName}`);
            const uploadedImage = await uploadBytes(storageRef, blobbed);
            const downloadURL = await getDownloadURL(uploadedImage.ref);
            setConfigurationUploadProgress(80);
            if (downloadURL) {
              console.log('Screenshot uploaded to storage.');
              moduleData.configuratorPreviewScreenshot = downloadURL;

              // After getting the screenshot, generate the PDF
              try {
                // Generate the PDF using the screenshot
                const pdfBlob = await generateWippenPdf(screenshotPng, moduleData);

                // Upload the PDF to Firebase Storage
                const pdfDownloadURL = await uploadPdfToStorage(pdfBlob, storage, moduleData.id!);

                // Save the PDF URL in the module data
                moduleData.outputPdf = pdfDownloadURL;
                console.log('PDF generated and uploaded to storage:', pdfDownloadURL);
              } catch (pdfError) {
                console.error('Error generating or uploading PDF:', pdfError);
                // Don't interrupt the flow if PDF generation fails
              }
            }
          } else {
            console.error('Failed to create blob from element.');
          }
        } catch (error) {
          console.error('Error when trying to upload screenshot to storage: ', error);
        }
      } else {
        console.log('Interface element not found.');
      }

      if (isSubmitted) {
        moduleData.isSubmitted = true;
      }

      setConfigurationUploadProgress(90);

      moduleData.projectTitle = projectTitle;

      if (editMode === true || autoSavedProgress === true) {
        const docRef = doc(db, `wippenConfigurations/${moduleData.id}`);
        await updateDoc(docRef, { ...moduleData });
        setConfigurationUploadProgress(-1);
        console.log('MODULE SUCCESSFULLY UPDATED!');
        if (previousVersionModuleCopy !== null) {
          const previousVersionID = `${previousVersionModuleCopy.id}_${Timestamp.now().toDate().getTime()}`;
          const previousVersionDocRef = doc(
            db,
            `wippenConfigurations/${moduleData.id}/previousVersions/${previousVersionID}`
          );
          await setDoc(previousVersionDocRef, { ...previousVersionModuleCopy });
          setConfigurationUploadProgress(-1);
          console.log(
            'PREVIOUS VERSION SUCCESSFULLY SAVED UNDER DOC: ',
            `wippenConfigurations/${moduleData.id}/previousVersions/${previousVersionID}`
          );
        }

        if (isSubmitted && moduleData.id != null) {
          deleteSavedProgress(moduleData.id);
        }
        setModuleSent(true);
        setVersionUpdated(false);
        setPreviousVersionModuleCopy(JSON.parse(JSON.stringify(moduleData)));
        setSendingModule(false);
        setConfigurationUploadProgress(-1);
        return;
      }

      if (previousVersionModuleCopy !== null) {
        const previousVersionID = `${previousVersionModuleCopy.id}_${Timestamp.now().toDate().getTime()}`;
        const previousVersionDocRef = doc(
          db,
          `wippenConfigurations/${moduleData.id}/previousVersions/${previousVersionID}`
        );
        await setDoc(previousVersionDocRef, { ...previousVersionModuleCopy });
        setConfigurationUploadProgress(-1);
        console.log(
          'PREVIOUS VERSION SUCCESSFULLY SAVED UNDER DOC: ',
          `wippenConfigurations/${moduleData.id}/previousVersions/${previousVersionID}`
        );
      }

      await updateDoc(doc(database, `wippenConfigurations/${moduleData.id}`), {
        ...moduleData,
      });
      console.log('MODULE SUCCESSFULLY SENT!');
      console.log('Module saved with ID: ', moduleData.id);
      const editLink = `${process.env.REACT_APP_BASE_URL}?k_id=${moduleData.id}`;
      console.log('Edit link: ', editLink);
      setConfigurationUploadProgress(-1);
      showSuccessSnackbar(t('configuration_sent_success'));

      if (isSubmitted && moduleData.id != null) {
        deleteSavedProgress(moduleData.id);
      }

      setSendingModule(false);
      setModuleSent(true);
      setSelectedModule(null);
      setModulePreviewData({ ...defaultModuleData });
      setUserInfo({ ...defaultModuleData.userInfo });
      setEditMode(false);
      setConfigurationUploadProgress(-1);
    } catch (error) {
      setSendingModule(false);
      setModuleSent(false);
      setConfigurationUploadProgress(-1);
      modulePreviewData.isSubmitted = false;
      console.error('Error when trying to send document to database: ', error);
      showErrorSnackbar(t('error_sending_module'));
    }
  };

  // const sendModuleToFirebase = async (
  //   moduleData: ModuleDataType,
  //   database: Firestore,
  //   storage: FirebaseStorage,
  //   usrInfo: UserInfo,
  //   { isSubmitted = false }: { isSubmitted?: boolean }
  // ) => {
  //   setSendingModule(true);

  //   setConfigurationUploadProgress(25);

  //   try {
  //     if (!moduleData.createdAt) {
  //       moduleData.createdAt = Timestamp.now();
  //     }

  //     moduleData.updatedAt = Timestamp.now();

  //     moduleData.userInfo = usrInfo;

  //     const interfaceElement = interfacePreviewRef.current;

  //     if (interfaceElement) {
  //       try {
  //         setConfigurationUploadProgress(20);

  //         let screenshotPng = '';
  //         console.log('Creating screenshot...');

  //         if (process.env.NODE_ENV === 'development') {
  //           console.log('---------------');
  //           // check what browser is being used
  //           if (isFirefox) {
  //             console.log('Firefox browser detected.');
  //           }
  //           if (isChrome) {
  //             console.log('Chrome browser detected.');
  //           }
  //           if (isSafari) {
  //             console.log('Safari browser detected.');
  //           }
  //           if (isMobile) {
  //             console.log('Mobile device detected.');
  //           }
  //           console.log('---------------');
  //         }

  //         if (!isFirefox && !isChrome) {
  //           setConfigurationUploadProgress(35);
  //           const firstScreenshotRun = await domToPng(interfaceElement, {
  //             quality: 0.9,
  //             backgroundColor: 'transparent',
  //           });
  //         }

  //         setConfigurationUploadProgress(50);

  //         const secondScreenshotRun = await domToPng(interfaceElement, {
  //           quality: 0.9,
  //           backgroundColor: 'transparent',
  //         }).then((dataUrl) => {
  //           screenshotPng = dataUrl;
  //         });

  //         setConfigurationUploadProgress(70);

  //         const blob = await fetch(screenshotPng);
  //         const blobbed = await blob.blob();

  //         if (blobbed) {
  //           const timestamp = new Date().getTime();
  //           const imageName = `preview_${timestamp}.png`;
  //           const storageRef = ref(storage, `images/module-preview-screenshots/${imageName}`);
  //           const uploadedImage = await uploadBytes(storageRef, blobbed);
  //           const downloadURL = await getDownloadURL(uploadedImage.ref);
  //           setConfigurationUploadProgress(90);
  //           if (downloadURL) {
  //             console.log('Screenshot uploaded to storage.');
  //             moduleData.configuratorPreviewScreenshot = downloadURL;
  //           }
  //         } else {
  //           console.error('Failed to create blob from element.');
  //         }
  //       } catch (error) {
  //         console.error('Error when trying to upload screenshot to storage: ', error);
  //       }
  //     } else {
  //       console.log('Interface element not found.');
  //     }

  //     if (isSubmitted) {
  //       moduleData.isSubmitted = true;
  //     }

  //     // if (moduleData.background.active && moduleData.background.base64Image) {
  //     //   const thumbnailBase64 = await createThumbnail(moduleData.background.base64Image, 320);
  //     //   const imageUrl = await uploadImageToStorage(
  //     //     moduleData.background.base64Image,
  //     //     storage,
  //     //     'module-foreground-images'
  //     //   );
  //     //   moduleData.background.imageUrl = imageUrl;

  //     //   if (typeof thumbnailBase64 === 'string') {
  //     //     const thumbnailUrl = await uploadImageToStorage(thumbnailBase64, storage, 'module-foreground-thumbnails');
  //     //     moduleData.background.thumbnail = thumbnailUrl;
  //     //   } else {
  //     //     console.log('Error: Thumbnail base64 for foreground did not return a string. Thumbnail creation aborted.');
  //     //   }
  //     // }

  //     setConfigurationUploadProgress(90);

  //     // if (moduleData.foregroundPrinting.active && moduleData.foregroundPrinting.base64Image) {
  //     //   const thumbnailBase64 = await createThumbnail(moduleData.foregroundPrinting.base64Image, 320);
  //     //   const imageUrlPrinting = await uploadImageToStorage(
  //     //     moduleData.foregroundPrinting.base64Image,
  //     //     storage,
  //     //     'module-foreground-printing-images'
  //     //   );
  //     //   moduleData.foregroundPrinting.imageUrl = imageUrlPrinting;

  //     //   if (typeof thumbnailBase64 === 'string') {
  //     //     const thumbnailUrl = await uploadImageToStorage(thumbnailBase64, storage, 'module-foreground-thumbnails');
  //     //     moduleData.foregroundPrinting.thumbnail = thumbnailUrl;
  //     //   } else {
  //     //     console.log(
  //     //       'Error: Thumbnail base64 for foreground printing did not return a string. Thumbnail creation aborted.'
  //     //     );
  //     //   }
  //     // }

  //     // if (defaultModuleData.background.color.hex !== interfaceBackgroundColor) {
  //     //   moduleData.background.color.hex = interfaceBackgroundColor;
  //     //   moduleData.background.color.ral = interfaceBackgroundColorRalNumber;
  //     // }

  //     moduleData.projectTitle = projectTitle;

  //     // moduleData.background.base64Image = null;
  //     // moduleData.foregroundPrinting.base64Image = null;

  //     if (editMode === true || autoSavedProgress === true) {
  //       const docRef = doc(db, `wippenConfigurations/${moduleData.id}`);
  //       await updateDoc(docRef, { ...moduleData });
  //       setConfigurationUploadProgress(-1);
  //       console.log('MODULE SUCCESSFULLY UPDATED!');
  //       if (previousVersionModuleCopy !== null) {
  //         const previousVersionID = `${previousVersionModuleCopy.id}_${Timestamp.now().toDate().getTime()}`;
  //         const previousVersionDocRef = doc(
  //           db,
  //           `wippenConfigurations/${moduleData.id}/previousVersions/${previousVersionID}`
  //         );
  //         await setDoc(previousVersionDocRef, { ...previousVersionModuleCopy });
  //         setConfigurationUploadProgress(-1);
  //         console.log(
  //           'PREVIOUS VERSION SUCCESSFULLY SAVED UNDER DOC: ',
  //           `wippenConfigurations/${moduleData.id}/previousVersions/${previousVersionID}`
  //         );
  //       }

  //       if (isSubmitted && moduleData.id != null) {
  //         deleteSavedProgress(moduleData.id);
  //       }
  //       setModuleSent(true);
  //       setVersionUpdated(false);
  //       setPreviousVersionModuleCopy(JSON.parse(JSON.stringify(moduleData)));
  //       setSendingModule(false);
  //       setConfigurationUploadProgress(-1);
  //       return;
  //     }

  //     if (previousVersionModuleCopy !== null) {
  //       const previousVersionID = `${previousVersionModuleCopy.id}_${Timestamp.now().toDate().getTime()}`;
  //       const previousVersionDocRef = doc(
  //         db,
  //         `wippenConfigurations/${moduleData.id}/previousVersions/${previousVersionID}`
  //       );
  //       await setDoc(previousVersionDocRef, { ...previousVersionModuleCopy });
  //       setConfigurationUploadProgress(-1);
  //       console.log(
  //         'PREVIOUS VERSION SUCCESSFULLY SAVED UNDER DOC: ',
  //         `wippenConfigurations/${moduleData.id}/previousVersions/${previousVersionID}`
  //       );
  //     }

  //     await updateDoc(doc(database, `wippenConfigurations/${moduleData.id}`), {
  //       ...moduleData,
  //     });
  //     // await updateDoc(docRef, { id: docRef.id });
  //     console.log('MODULE SUCCESSFULLY SENT!');
  //     console.log('Module saved with ID: ', moduleData.id);
  //     const editLink = `${process.env.REACT_APP_BASE_URL}?k_id=${moduleData.id}`;
  //     console.log('Edit link: ', editLink);
  //     setConfigurationUploadProgress(-1);
  //     showSuccessSnackbar(t('configuration_sent_success'));

  //     if (isSubmitted && moduleData.id != null) {
  //       deleteSavedProgress(moduleData.id);
  //     }

  //     setSendingModule(false);
  //     // setActiveStepIndex(0);
  //     setModuleSent(true);
  //     setSelectedModule(null);
  //     setModulePreviewData({ ...defaultModuleData });
  //     setUserInfo({ ...defaultModuleData.userInfo });
  //     setEditMode(false);
  //     setConfigurationUploadProgress(-1);
  //   } catch (error) {
  //     setSendingModule(false);
  //     setModuleSent(false);
  //     setConfigurationUploadProgress(-1);
  //     modulePreviewData.isSubmitted = false;
  //     console.error('Error when trying to send document to database: ', error);
  //     showErrorSnackbar(t('error_sending_module'));
  //   }
  // };

  const autoSaveProgress = async (docId: string, moduleData: ModuleDataType) => {
    setSendingModule(true);
    const autoSaveRef = doc(db, `wippenConfigurations/${docId}/_autoSavedData/default`);
    try {
      await setDoc(
        autoSaveRef,
        {
          ...moduleData,
          updatedAt: Timestamp.now(),
          id: docId,
        },
        { merge: true }
      );

      console.log('Auto save progress successful!');
      setSendingModule(false);
    } catch (e) {
      console.log('ERROR: There was an error while trying to auto save progress: ', e);
      setSendingModule(false);
    }
  };

  const deleteSavedProgress = async (docId: string) => {
    const autoSaveRef = doc(db, `wippenConfigurations/${docId}/_autoSavedData/default`);
    await deleteDoc(autoSaveRef);
  };

  const createNewFirebaseDoc = async (moduleData: ModuleDataType) => {
    setSendingModule(true);

    const docRef = await addDoc(collection(db, 'wippenConfigurations'), {
      ...moduleData,
      isSubmitted: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    await updateDoc(docRef, { id: docRef.id });

    await autoSaveProgress(docRef.id, moduleData);

    navigate(`?k_id=${docRef.id}&autosaved=true`);

    setAutoSavedProgress(true);

    console.log('Success! Configurator saved with ID: ', docRef.id);

    setModulePreviewData((prevData) => ({ ...prevData, id: docRef.id }));
    setSendingModule(false);
    return docRef;
  };

  const resetConfigurator = () => {
    setModuleSent(false);
    setSendingModule(false);
    setAutoSavedProgress(false);
    setModulePreviewData({ ...defaultModuleData });
    setIsReadOnly(false);
    setSelectedModule(null);

    setEditMode(false);
    setPreviousVersionModuleCopy(null);
    navigate('/');
    setCurrentStep(0);
    setResetChangesConfirmationDialogOpen(false);
  };

  const handleGoNextStep = async () => {
    let docId = modulePreviewData.id;

    if (currentStep === 0 && !editMode && userDismissedHints === false) {
      setHintsActive(true);
    }

    // if (
    //   currentStep === 2 &&
    //   modulePreviewData.assembly.connectorPlug === 'individual' &&
    //   (modulePreviewData.assembly.individualConnectorPlug === null ||
    //     modulePreviewData.assembly.individualConnectorPlug === '')
    // ) {
    //   setIndividualConnectorPlugInputError(true);
    //   showErrorSnackbar(t('error_please_fill_out_connector_plug'));
    //   return;
    // }

    // if (currentStep === 1 && interfacePreviewRef.current !== null) {
    //   // uploadInterfacePreviewScreenshot();
    // }

    if (currentStep === 1 && !docId) {
      console.log('(Starting auto save progress.)');
      console.log('Creating new document in database for the configurator...');
      const newDocRef = await createNewFirebaseDoc(modulePreviewData);
      docId = newDocRef.id;
    } else if (currentStep === 1 && docId) {
      console.log('Auto saving progress...');
      await autoSaveProgress(docId, modulePreviewData);
    }

    // if (currentStep === 2 && docId) {
    //   console.log('Auto saving progress...');
    //   await autoSaveProgress(docId, modulePreviewData);
    // }

    if (currentStep === 2) {
      try {
        await validationSchema.validate(userInfo, { abortEarly: false });

        sendModuleToFirebase(modulePreviewData, db, firbaseStorage, userInfo, {
          isSubmitted: true,
        });
      } catch (validationErrors) {
        const updatedErrors = (validationErrors.inner as ValidationError[]).reduce<FormErrors>((acc, error) => {
          acc[error.path] = error.message;
          return acc;
        }, {});
        setErrors(updatedErrors);
        showErrorSnackbar(t('error_please_fill_out_all_mandatory_fields'));

        return;
      }
    }

    if (currentStep < steps.length - 1) {
      setDirection('left');
      setCurrentStep((prevActiveStep) => prevActiveStep + 1);
      setTransitionKey((prevKey) => prevKey + 1);
    }
  };

  const askUserIfHeWantsToResetChanges = () => {
    setResetChangesConfirmationDialogOpen(true);
  };

  const handleGoBackStep = () => {
    if (currentStep === 1) {
      if (autoSavedProgress === true || modulePreviewData.isSubmitted === true || editMode === true) {
        askUserIfHeWantsToResetChanges();
        return;
      }
    }
    setDirection('right');
    setCurrentStep((prevActiveStep) => prevActiveStep - 1);
    setTransitionKey((prevKey) => prevKey + 1);
  };

  const handleCloseIconPicker = () => {
    setIconSearchText('');
    setIconPickerOpen(null);
    customPopover.onClose();
  };

  const handleSelectNoSymbol = () => {
    handleButtonsChange(iconPickerOpen?.ledId as number, undefined, undefined, null);
    setIconSearchText('');
    setIconPickerOpen(null);
  };

  // const getBase64 = (file: File): Promise<string> => {
  //   return new Promise<string>((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.readAsDataURL(file);
  //     reader.onload = () => resolve(reader.result as string);
  //     reader.onerror = error => reject(error);
  //   });
  // };

  const handleUploadSymbol = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Uploading symbol');
    if (isReadOnly || !event.target.files) return;

    const selectedFile = event.target.files[0];

    if (!selectedFile) return;

    const handleOpenCustomSymbolDialogConfirmation = (file: File) => {
      setUserUploadedCustomSymbol(file);
      setOpenConfirmationDialogForUploadCustomSymbol(true);
    };

    console.log('opening dialog');
    handleOpenCustomSymbolDialogConfirmation(selectedFile);
  };

  const getIconImageForIconDisplayBig = (buttonType: string) => {
    if (selectedButtonIndex === null) return null;

    const selectedButton = modulePreviewData.elements.symbols[selectedButtonIndex];
    if (!selectedButton?.icon) return null;

    if (selectedButton.useCustomSymbol) {
      const svgFile = modulePreviewData.userUploadedFiles.find((file) => file.id === selectedButton.icon);
      return svgFile?.base64 || svgFile?.url || null;
    }

    const icon = iconMap[selectedButton.icon || 0];
    return `${getMimeType(icon)}${uint8ToBase64(icon, 'getIconImageForIconDisplayBig')}`;
  };

  const handleColorPickerChange = (color: ColorResult, event: React.ChangeEvent<HTMLInputElement>, ledId: number) => {
    handleButtonsChange(ledId, undefined, color.hex);
  };

  const handleDisplayChange = (selection: string) => {
    const displaySelected = displayList.find((display) => display.id === selection);
    const displayType = displaySelected?.type;
    if (isReadOnly) {
      return;
    }

    setModulePreviewData((prevData) => {
      const newData = { ...prevData };

      return newData;
    });
  };

  const handleDisplayClick = (e: React.MouseEvent<HTMLElement>) => {
    setDisplaySelectorOpen(true);
    setAnchorEl(e.currentTarget);
    setIconPickerOpen(null);
  };

  const handleUserInfoChange = (event: { target: { name: string; value: any } }) => {
    const { name, value } = event.target;

    if (name === 'telephoneNumber' && !/^\+?\d*$/.test(value)) {
      return;
    }

    const newUserInfo = { ...userInfo, [name]: value };

    setUserInfo(newUserInfo);

    validationSchema
      .validateAt(name, newUserInfo)
      .then(() => {
        setErrors({ ...errors, [name]: '' });
      })
      .catch((err) => {
        setErrors({ ...errors, [name]: err.message });
      });
  };

  const handleClickHintsUnderstoodButton = () => {
    setHintsActive(false);
    setUserDismissedHints(true);
    hintPopover.onClose();

    if (buttonRef !== null) {
      setTimeout(() => {
        buttonRef.click();
      }, 30);
    }
  };

  interface ButtonsLedsEditorsProps {
    ledId: number;
    ledActive: boolean;
    currentLedColor: string;
    iconId: number | null;
  }

  const ButtonsLedsEditors: React.FC<ButtonsLedsEditorsProps> = ({ ledId, ledActive, currentLedColor, iconId }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
      {colorPickerOpen?.ledId === ledId && (
        <Box
          ref={containerRef}
          style={{
            position: 'absolute',
            zIndex: 2,
            transform: `translate(11rem, ${36 - ledContainerScrollOffset}px)`,
          }}
        >
          <CustomGithubPicker
            colors={['#ffffff', '#ffff00', '#f79340', '#db1845', '#27f462', '#1278ea']}
            titles={[t('white'), t('yellow'), t('amber'), t('red'), t('green'), t('blue')]}
            onChange={(color, event) => handleColorPickerChange(color, event, ledId)}
            width="fit-content"
          />
        </Box>
      )}

      <FormControlLabel
        control={
          <Switch
            size="small"
            color="success"
            disabled={isReadOnly}
            defaultChecked={ledActive}
            onChange={(e) => handleButtonsChange(ledId, e.target.checked)}
          />
        }
        label={
          <Typography sx={{ fontSize: '0.8rem', minWidth: '2.6rem' }} variant="body1">
            {`LED ${ledId}`}
          </Typography>
        }
      />
      <Stack direction="row" spacing={1}>
        <IconDisplay
          label={t('symbol')}
          isReadOnly={isReadOnly}
          iconImage={
            iconId && iconMap[iconId]
              ? `${getMimeType(iconMap[iconId])}${uint8ToBase64(iconMap[iconId], 'ButtonsLedEditors')}`
              : null
          }
          onClick={() => {
            setIconPickerOpen({
              ledId,
              side: null,
              showOnlyBargraphSymbols: true,
            });
          }}
        />
        <ColorDisplay
          isReadOnly={isReadOnly}
          label={t('color')}
          tooltipText={t('tooltip_unicolor_leds')}
          color={currentLedColor === 'transparent' || currentLedColor === '' ? '#ffffff' : currentLedColor}
          onClick={() => {
            setColorPickerOpen({ ledId, side: 'middle' });
          }}
        />
      </Stack>
    </Box>
  );

  useEffect(() => {
    if (colorPickerOpen?.side === undefined) {
      setPickerPosition({ top: 0, left: 0 });
    }
  }, [colorPickerOpen]);

  const calculatePickerTop = () => {
    let fixedPickerPosition = pickerPosition.top;
    fixedPickerPosition -= 75;
    if (isMobileScreen) {
      fixedPickerPosition += 100;
      if (colorPickerOpen?.side === 'right') {
        fixedPickerPosition -= 150;
        return fixedPickerPosition;
      }
      return fixedPickerPosition;
    }
    if (isSmallWebScreen) {
      fixedPickerPosition -= 145;
      if (colorPickerOpen?.side === 'right') {
        return fixedPickerPosition;
      }
      return fixedPickerPosition;
    }

    if (colorPickerOpen?.side === 'right') {
      fixedPickerPosition -= 345;
      return fixedPickerPosition;
    }
    return fixedPickerPosition;
  };

  const calculatePickerLeft = () => {
    let fixedPickerPosition = pickerPosition.left;
    fixedPickerPosition -= 165;

    if (isMobileScreen) {
      if (colorPickerOpen?.side === 'right') {
        return fixedPickerPosition;
      }
      return fixedPickerPosition;
    }
    if (isSmallWebScreen) {
      if (colorPickerOpen?.side === 'right') {
        return fixedPickerPosition;
      }
      fixedPickerPosition -= 35;
      return fixedPickerPosition;
    }

    fixedPickerPosition -= 20;

    if (colorPickerOpen?.side === 'right') {
      return fixedPickerPosition;
    }

    return fixedPickerPosition;
  };

  const hints = [
    {
      element: '.hint-1',
      hint: `${t('hint_symbol_and_color')}`,
      hintPosition: 'middle-middle',
    },
  ];

  useEffect(() => {
    if (currentStep === 1 && !editMode && !dissmissHints) {
      setDissmissHints(true);

      setDissmissHints(false);
    }
  }, [currentStep, dissmissHints, editMode]);

  const pickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (pickerRef.current) {
      const { scrollTop } = pickerRef.current;
      setLedContainerScrollOffset(scrollTop);
    }
  }, [colorPickerOpen, pickerRef]);

  const uploadFile = async (file: File, customIconDescription: string, { userApprovedUploadToDatabase = false }) => {
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        const id = Date.now();
        const fileName = file.name;
        const storage = getStorage();
        const storageRef = ref(storage, `icons/${id}_${fileName}`);
        const uploadTask = uploadBytes(storageRef, file);

        const isStatusLed = iconPickerOpen?.side === 'left' || iconPickerOpen?.side === 'right';
        const statusLedSide = iconPickerOpen?.side;

        uploadTask.then((snapshot) => {
          getDownloadURL(snapshot.ref).then((downloadURL) => {
            const newFile: UserUploadedFile = {
              id,
              fileName,
              fileType: file.type,
              url: downloadURL,
              base64: null,
              description: customIconDescription,
              userApprovedUploadToDatabase,
            };

            setModulePreviewData((prevData) => {
              const newData = { ...prevData };

              if (!newData.userUploadedFiles) {
                newData.userUploadedFiles = [];
              }
              newData.userUploadedFiles.push(newFile);

              if (selectedButtonIndex !== null && selectedButtonIndex !== undefined) {
                newData.elements.symbols[selectedButtonIndex].icon = newFile.id;
                newData.elements.symbols[selectedButtonIndex].useCustomSymbol = true;
              }

              return newData;
            });

            setUserUploadedCustomSymbolDescription('');
          });
        });
      };
      reader.readAsDataURL(file);
      showSuccessSnackbar(t('icon_uploaded_successfully_snackbar_message'));
    } catch (error) {
      console.error('Error when trying to upload custom icon:', error);
      showErrorSnackbar(t('error_uploading_icon_snackbar_message'));
    }

    setIconPickerOpen(null);
  };

  const handleCloseDialogForCustomSymbolConfirmation = (agree: boolean) => {
    setOpenConfirmationDialogForUploadCustomSymbol(false);

    if (!userUploadedCustomSymbol) {
      console.log('Error: No file selected to upload. Upload aborted.');
      return;
    }
    uploadFile(userUploadedCustomSymbol, userUploadedCustomSymbolDescription, { userApprovedUploadToDatabase: agree });
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer | null) => {
    if (!buffer) return '';
    const binary = new Uint8Array(buffer);
    return `data:image/svg+xml;base64,${btoa(String.fromCharCode(...binary))}`;
  };

  if (modulePreviewData === null) {
    return <div>loading...</div>;
  }

  return (
    <GlobalContainer className="global-container" id="miunske-konfigurator-stepper">
      <div
        className="hidden-screenshot-container"
        style={{
          width: 'fit-content',
          position: 'absolute',
          zIndex: -1000,
          transform: 'translate(-10000px, -10000px)',
        }}
      >
        <div
          style={{
            width:
              modulePreviewData.rotation === '90'
                ? '15rem'
                : modulePreviewData.rotation === '180'
                ? 'auto'
                : modulePreviewData.rotation === '270'
                ? '15rem'
                : modulePreviewData.rotation === '0'
                ? 'auto'
                : undefined,
          }}
          id="interface-preview-screenshot"
          ref={interfacePreviewRef}
        >
          {modulePreviewData !== null && (
            <InterfacePreview
              screenshotMode
              handleClickHintDot={() => {}}
              hintsActive={false}
              id="interface-preview"
              loadingIcons={loadingIcons}
              arrayOfIcons={arrayOfIcons}
              modulePreviewData={modulePreviewData}
              handleElementClick={handleRockerSwitchLightClick}
              handleDisplayClick={handleDisplayClick}
              displayList={displayList}
            />
          )}
        </div>
      </div>
      <Hints
        // enabled={currentStep === 1 && !editMode && !dissmissHints && true}
        enabled
        hints={hints}
        onClose={() => setDissmissHints(true)}
        options={{
          hintButtonLabel: t('understood_button'),
        }}
      />

      {!isTabletScreen && (
        <Stepper activeStep={currentStep}>
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel sx={{ flexDirection: 'column', gap: '0.8rem' }}>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}
      {resetChangesConfirmationDialogOpen && (
        <Dialog
          open={resetChangesConfirmationDialogOpen}
          onClose={() => setResetChangesConfirmationDialogOpen(false)}
          aria-labelledby="discard-changes-dialog-title"
          aria-describedby="discard-changes-dialog-description"
        >
          <DialogTitle id="discard-changes-dialog-title">{t('alert_discard_changes_title')}</DialogTitle>
          <DialogContent>
            <DialogContentText id="discard-changes-dialog-description">
              {t('alert_discard_changes_body')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setResetChangesConfirmationDialogOpen(false)}
              // color="black"
              autoFocus
            >
              {t('cancel')}
            </Button>
            <Button variant="contained" color="error" onClick={resetConfigurator}>
              {t('discard_changes')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Dialog
        open={openConfirmationDialogForUploadCustomSymbol}
        onClose={() => handleCloseDialogForCustomSymbolConfirmation(false)}
      >
        <DialogTitle>{t('confirm_upload_title')}</DialogTitle>
        <DialogContent
          sx={{
            py: '1rem',
          }}
        >
          {t('symbol_description_message')}
        </DialogContent>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={userUploadedCustomSymbolDescription}
            onChange={(e) => setUserUploadedCustomSymbolDescription(e.target.value)}
          />
        </DialogContent>
        <DialogContent
          sx={{
            pt: '1rem',
          }}
        >
          {t('confirm_upload_message', { fileName: userUploadedCustomSymbol?.name })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseDialogForCustomSymbolConfirmation(false)}>{t('no')}</Button>
          <Tooltip
            title={userUploadedCustomSymbolDescription === '' ? t('provide_a_description_to_save_to_database') : ''}
            arrow
          >
            <span>
              <Button
                disabled={userUploadedCustomSymbolDescription === ''}
                onClick={() => handleCloseDialogForCustomSymbolConfirmation(true)}
              >
                {t('yes')}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
      <Dialog open={projectTitleDialogOpen} onClose={handleCloseProjectTitleDialog}>
        <DialogTitle>{t('insert_a_title_for_your_project')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('project_title')}
            type="text"
            fullWidth
            value={projectTitle}
            onChange={(e) => setProjectTitle(e.target.value)}
            sx={contactFormInputsStyle}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProjectTitleDialog} color="primary">
            {t('cancel')}
          </Button>
          <Button
            // TODO: PROYECT TITLE
            // disabled={projectTitle === ''}
            variant="contained"
            onClick={handleProjectTitleContinue}
            color="primary"
          >
            {t('continue')}
          </Button>
        </DialogActions>
      </Dialog>

      <ContentArea className="content-area">
        <ContentTransition direction={direction} key={transitionKey} transitionkey={transitionKey}>
          {currentStep === 0 && (
            <InterfaceContentBox className="interface-content">
              <Stack
                direction={isMobileScreen ? 'column' : 'row'}
                gap={3}
                sx={{
                  pb: '2rem',
                  marginLeft: isMobileScreen ? '0.5rem' : '0rem',
                }}
              >
                {/* <Stack direction="row" justifyContent="space-between">
                  <Box
                    className="pilot-miunske-logo"
                    sx={{
                      backgroundImage: `url(${process.env.REACT_APP_BASE_URL}assets/images/logos/pilot-miunske-logo.png)`,
                      width: '5rem',
                      height: '3rem',

                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'contain',
                    }}
                  />
                </Stack> */}
                <Stack justifyContent="center" gap={1}>
                  {isTabletScreen && (
                    <>
                      <InterfaceContentTitle variant="h5">{steps[currentStep].label}</InterfaceContentTitle>
                      <Divider flexItem />
                    </>
                  )}

                  <Stack direction="row" justifyContent="space-between">
                    <InterfaceContentTitle variant="h5">{t('select_switch')}</InterfaceContentTitle>
                  </Stack>
                </Stack>
              </Stack>
              <Grid justifyContent="center" sx={{ height: '100%', pb: '3.2rem' }} container spacing={4}>
                {moduleList.map((module, index) => (
                  <GridItem item xs={1} sm={4} key={index}>
                    <Paper
                      sx={{
                        minHeight: '5.8rem',
                        cursor: 'pointer',
                        backgroundColor: selectedModule?.id === module.id ? 'rgb(0,106,178,43%)' : '#fff',
                        border:
                          selectedModule?.id === module.id ? '2px solid rgb(0,106,178,37%)' : '2px solid transparent',
                      }}
                      elevation={4}
                      onClick={() => {
                        setSelectedModule(module);
                      }}
                    >
                      {module.image && <ModuleThumbnailImage src={module.image} alt={t(module.name)} />}
                    </Paper>
                    <Typography
                      sx={{
                        lineHeight: '1.4rem',
                        pt: '0.25rem',
                        pb: '0.5rem',
                        fontSize: {
                          xs: '0.7rem',
                          md: '0.9rem',
                        },
                      }}
                    >
                      <Box sx={{ fontWeight: '650' }}>{module.type.toLocaleUpperCase()}</Box>
                      <Box sx={{ lineHeight: '0.8rem' }}>{t(module.name)}</Box>
                    </Typography>
                  </GridItem>
                ))}
              </Grid>
            </InterfaceContentBox>
          )}

          {currentStep === 1 && (
            <InterfaceContentBox className="interface-content">
              {/* ICON PICKER DIALOG */}
              <Dialog
                slots={{ backdrop: Backdrop }}
                transitionDuration={0}
                slotProps={{
                  backdrop: {
                    sx: {
                      backgroundColor: 'transparent',
                    },
                  },
                }}
                PaperProps={{
                  sx: {
                    height: iconPickerOpen?.showOnlyBargraphSymbols === true ? '20.9rem' : '100%',
                    maxWidth: '37.5rem',
                    width: '100%',
                  },
                }}
                open={iconPickerOpen !== null}
                onClose={handleCloseIconPicker}
                aria-labelledby="icon-picker-dialog-title"
                aria-describedby="icon-picker-dialog-description"
              >
                <DialogTitle sx={{ paddingBottom: '0 !important' }} id="icon-picker-dialog-title">
                  {t('choose_a_symbol')}
                  {(iconPickerOpen?.showOnlyBargraphSymbols === false ||
                    iconPickerOpen?.side === 'left' ||
                    iconPickerOpen?.side === 'right') && (
                    <Box my={2}>
                      <TextField
                        fullWidth
                        placeholder={t('symbol_search')}
                        value={iconSearchText}
                        onChange={(e) => setIconSearchText(e.target.value)}
                      />
                    </Box>
                  )}
                </DialogTitle>
                <DialogContent>
                  {modulePreviewData.userUploadedFiles &&
                    modulePreviewData.userUploadedFiles.length > 0 &&
                    iconPickerOpen?.showOnlyBargraphSymbols === false && (
                      <>
                        <Typography sx={{ padding: '0.5rem' }}>{t('user_uploaded_icons')}</Typography>
                        <Box
                          sx={{
                            borderRadius: '10px',
                            border: '1px solid #eaeaea',
                            padding: '0',
                            marginBottom: '1.5rem',
                            overflowX: 'hidden',
                          }}
                        >
                          <Grid container spacing={2}>
                            {modulePreviewData.userUploadedFiles.map((file, index) => (
                              <Grid
                                item
                                xs={3}
                                sm={iconPickerOpen?.showOnlyBargraphSymbols === true ? 2.2 : 1.47}
                                key={index}
                              >
                                <Button
                                  fullWidth
                                  title={file.fileName}
                                  onClick={() => {
                                    if (iconPickerOpen?.side === null) {
                                      handleButtonsChange(
                                        iconPickerOpen.ledId as number,
                                        undefined,
                                        undefined,
                                        file.id
                                      );
                                    }

                                    handleCloseIconPicker();
                                  }}
                                  sx={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    height: '100%',
                                    textAlign: 'center',
                                    aspectRatio: iconPickerOpen?.showOnlyBargraphSymbols === true ? '1/1' : 'auto',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      flexDirection: 'column',
                                      overflow: 'hidden',
                                      width: '100%',
                                      height: '100%',
                                      ...(iconPickerOpen?.showOnlyBargraphSymbols === true && {
                                        width: '100%',
                                        height: '100%',
                                        padding: isMobileScreen ? '1rem' : '1.4rem',
                                      }),
                                    }}
                                  >
                                    <img
                                      style={{ aspectRatio: '1/1' }}
                                      src={file.url}
                                      alt={`${file.fileName}`}
                                      height="100%"
                                      width="80%"
                                    />
                                  </Box>
                                </Button>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </>
                    )}

                  {arrayOfIcons.length !== 0 && (
                    <Box
                      sx={{
                        borderRadius: '10px',
                        border: '1px solid #eaeaea',
                        padding: '0',
                        overflowX: 'hidden',
                      }}
                    >
                      <Grid
                        container
                        sx={{
                          padding: '0 1rem',
                        }}
                        spacing={2}
                      >
                        {arrayOfIcons
                          .filter((iconData) => {
                            /// iconData[0] RecNo
                            /// iconData[1] ID
                            /// iconData[2] Name
                            /// iconData[3] Description
                            /// iconData[4] Category
                            /// iconData[5] Tags
                            /// iconData[6] English Description

                            const category = iconData[4];
                            const id = iconData[1];
                            const description = iconData[5].toLowerCase();
                            const descriptionWithEnglishText = iconData[3].length >= 1 ? iconData[3].toLowerCase() : '';

                            const isIconSearchTextNumeric = !Number.isNaN(parseInt(iconSearchText, 10));
                            const iconSearchTextNumber = parseInt(iconSearchText, 10);

                            if (iconPickerOpen?.showOnlyBargraphSymbols === true) {
                              return (
                                (description.includes(iconSearchText.toLowerCase()) ||
                                  descriptionWithEnglishText.includes(iconSearchText.toLowerCase())) &&
                                category === 3
                              );
                            }

                            return (
                              description.includes(iconSearchText.toLowerCase()) ||
                              descriptionWithEnglishText.includes(iconSearchText.toLowerCase()) ||
                              (isIconSearchTextNumeric && id === iconSearchTextNumber)
                            );
                          })
                          .filter((iconData) => {
                            if (iconData === undefined) {
                              return false;
                            }
                            if (iconData === null) {
                              return false;
                            }

                            if (iconData[1] === null) {
                              return false;
                            }
                            if (iconData[1] === undefined) {
                              return false;
                            }
                            return true;
                          })
                          .map((iconData, index) => (
                            <Grid
                              item
                              xs={3}
                              sm={iconPickerOpen?.showOnlyBargraphSymbols === true ? 2 : 1.47}
                              key={index}
                            >
                              <Button
                                fullWidth
                                title={iconData[3]}
                                onClick={() => {
                                  if (iconPickerOpen?.side === null) {
                                    handleButtonsChange(
                                      iconPickerOpen?.ledId as number,
                                      undefined,
                                      undefined,
                                      iconData[1]
                                    );
                                  }

                                  handleCloseIconPicker();
                                }}
                                sx={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  height: '100%',
                                  textAlign: 'center',
                                  aspectRatio: iconPickerOpen?.showOnlyBargraphSymbols === true ? '1/1' : 'auto',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                    width: '100%',
                                    height: '100%',
                                    ...(iconPickerOpen?.showOnlyBargraphSymbols === true && {
                                      width: '100%',
                                      height: '100%',
                                      padding: isMobileScreen ? '1rem' : '1.4rem',
                                    }),
                                  }}
                                >
                                  <img
                                    style={{ aspectRatio: '1/1' }}
                                    src={`${getMimeType(iconMap[iconData[1]])}${uint8ToBase64(
                                      iconMap[iconData[1]],
                                      index
                                    )}`}
                                    alt={`${iconData[3]}`}
                                    height="100%"
                                    width="80%"
                                  />
                                </Box>
                              </Button>
                            </Grid>
                          ))}
                      </Grid>
                    </Box>
                  )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between' }}>
                  <Stack direction="row">
                    {iconPickerOpen?.showOnlyBargraphSymbols === false && (
                      <Stack sx={{ transform: 'translateY(10px)' }} direction="column">
                        <Button color="primary" variant="contained" component="label">
                          {t('upload_symbol')}
                          <input type="file" accept="image/svg+xml" hidden onChange={handleUploadSymbol} />
                        </Button>
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: 'grey',
                            mt: 0.2,
                            width: '100%',
                            textAlign: 'center',
                          }}
                        >
                          {t('custom_svg_file_formant_hint')}
                        </Typography>
                      </Stack>
                    )}

                    <Button sx={{ ml: 1 }} color="error" onClick={handleSelectNoSymbol}>
                      {t('no_symbol')}
                    </Button>
                  </Stack>

                  <Button onClick={handleCloseIconPicker}>{t('close')}</Button>
                </DialogActions>
              </Dialog>

              {/* Buttons Editors Popover */}
              <CustomPopover
                open={customPopover.open}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                hiddenArrow={isMobileScreen}
                arrow={arrowPositionOfPopover()}
                sx={{
                  transform: isMobileScreen ? 'none' : 'translate(0.2rem, 0rem) !important',
                  ...(isMobileScreen && { left: '15% !important' }),
                }}
              >
                <Box sx={{ p: '0.5rem 0.8rem' }}>
                  {colorPickerOpen?.ledId === 0 && selectedButtonIndex !== -1 && (
                    <Box
                      ref={containerRef}
                      style={{
                        position: 'absolute',
                        zIndex: 2,
                        transform: isMobileScreen ? 'translate(1%, 6rem)' : 'translate(1rem, 6.5rem)',
                      }}
                    >
                      <SketchPicker
                        disableAlpha
                        color={
                          selectedButtonIndex === null
                            ? '#ffffff'
                            : modulePreviewData.elements.symbols[selectedButtonIndex].color
                        }
                        styles={{
                          default: {
                            picker: {
                              cursor: 'pointer',
                            },
                          },
                        }}
                        onChange={(color: ColorResult, event: React.ChangeEvent<HTMLInputElement>) =>
                          handleColorPickerChange(color, event, 0)
                        }
                      />
                    </Box>
                  )}
                  {/* {colorPickerOpen?.ledId === 0 && selectedButtonIndex === -1 && (
                    <Box
                      ref={containerRef}
                      style={{
                        position: 'absolute',
                        zIndex: 2,
                        transform: 'translate(5.5rem, -8.5rem)',
                      }}
                    >
                      <SketchPicker
                        disableAlpha
                        color={selectedButtonIndex === null ? '#ffffff' : modulePreviewData.elements.bigButton.color}
                        styles={{
                          default: {
                            picker: {
                              cursor: 'pointer',
                            },
                          },
                        }}
                        onChange={(color: ColorResult, event: React.ChangeEvent<HTMLInputElement>) =>
                          handleColorPickerChange(color, event, 0)
                        }
                      />
                    </Box>
                  )} */}
                  {selectedButtonIndex !== null && selectedButtonIndex !== -1 && (
                    <Stack direction="column" spacing={1.3}>
                      <Stack justifyContent="space-between" direction="row">
                        <Typography
                          sx={{ color: theme.palette.primary.main }}
                          fontSize="1rem"
                          textAlign="start"
                          width="100%"
                          fontWeight="bold"
                        >
                          {selectedButtonIndex === 1 &&
                          modulePreviewData.elements.symbols[selectedButtonIndex - 1]?.active
                            ? 'Symbol A'
                            : selectedButtonIndex === 0 &&
                              modulePreviewData.elements.symbols[selectedButtonIndex]?.active
                            ? 'Symbol B'
                            : 'Symbol A'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" sx={{ height: '3.5rem' }} gap={1}>
                        <IconDisplayBig
                          label={t('symbol')}
                          isReadOnly={isReadOnly}
                          iconImage={getIconImageForIconDisplayBig('smallButton')}
                          isCustomIcon={!!modulePreviewData.elements.symbols[selectedButtonIndex]?.useCustomSymbol}
                          onClick={() => {
                            setIconPickerOpen({
                              ledId: 0,
                              side: null,
                              showOnlyBargraphSymbols: false,
                            });
                          }}
                        />
                        {/* <ColorDisplayBig
                          label={t('color')}
                          isReadOnly={isReadOnly}
                          color={
                            selectedButtonIndex === null
                              ? '#ffffff'
                              : modulePreviewData.elements.smallButtons[selectedButtonIndex]?.color || '#ffffff'
                          }
                          onClick={() => {
                            setColorPickerOpen({ ledId: 0, side: 'middle' });
                          }}
                        /> */}
                      </Stack>
                      {/* <Stack direction="row" gap={1}>
                        <FormControlLabel
                          labelPlacement="start"
                          sx={{ m: 0, transform: 'translateX(0.2rem)' }}
                          control={
                            <Switch
                              size="small"
                              color="success"
                              disabled={isReadOnly}
                              checked={
                                modulePreviewData != null && selectedButtonIndex !== null
                                  ? modulePreviewData.elements.smallButtons[selectedButtonIndex]?.framedBorder
                                  : false
                              }
                              onChange={(e) => handleChangeButtonFrameBorder(e)}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: '0.65rem' }} variant="body1">
                              {t('frame_border')}
                            </Typography>
                          }
                        />

                        <FormControlLabel
                          labelPlacement="start"
                          sx={{ m: 0, transform: 'translateX(0.45rem)' }}
                          control={
                            <Switch
                              size="small"
                              color="success"
                              disabled={isReadOnly}
                              checked={
                                modulePreviewData != null && selectedButtonIndex !== null
                                  ? modulePreviewData.elements.smallButtons[selectedButtonIndex]?.embossing
                                  : false
                              }
                              onChange={(e) => handleChangeButtonEmbossing(e)}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: '0.65rem' }} variant="body1">
                              {t('embossing')}
                            </Typography>
                          }
                        />
                      </Stack> */}

                      {/* <Box sx={{ pb: '0.2rem', pt: '0.2rem' }}>
                        <Divider />
                      </Box> */}

                      {/* <ButtonsLedsEditors
                        ledId={1}
                        ledActive={
                          selectedButtonIndex !== null &&
                          modulePreviewData.elements.smallButtons[selectedButtonIndex]?.leds
                            ? modulePreviewData.elements.smallButtons[selectedButtonIndex].leds![0].active
                            : false
                        }
                        currentLedColor={
                          selectedButtonIndex !== null &&
                          modulePreviewData.elements.smallButtons[selectedButtonIndex]?.leds
                            ? modulePreviewData.elements.smallButtons[selectedButtonIndex].leds![0].color
                            : '#ffffff'
                        }
                        iconId={
                          selectedButtonIndex !== null &&
                          modulePreviewData.elements.smallButtons[selectedButtonIndex]?.leds
                            ? modulePreviewData.elements.smallButtons[selectedButtonIndex].leds![0].icon
                            : null
                        }
                      />
                      <ButtonsLedsEditors
                        ledId={2}
                        ledActive={
                          selectedButtonIndex !== null &&
                          modulePreviewData.elements.smallButtons[selectedButtonIndex]?.leds
                            ? modulePreviewData.elements.smallButtons[selectedButtonIndex].leds![1].active
                            : false
                        }
                        currentLedColor={
                          selectedButtonIndex !== null &&
                          modulePreviewData.elements.smallButtons[selectedButtonIndex]?.leds
                            ? modulePreviewData.elements.smallButtons[selectedButtonIndex].leds![1].color
                            : '#ffffff'
                        }
                        iconId={
                          selectedButtonIndex !== null &&
                          modulePreviewData.elements.smallButtons[selectedButtonIndex]?.leds
                            ? modulePreviewData.elements.smallButtons[selectedButtonIndex].leds![1].icon
                            : null
                        }
                      />
                      <ButtonsLedsEditors
                        ledId={3}
                        ledActive={
                          selectedButtonIndex !== null &&
                          modulePreviewData.elements.smallButtons[selectedButtonIndex]?.leds
                            ? modulePreviewData.elements.smallButtons[selectedButtonIndex].leds![2].active
                            : false
                        }
                        currentLedColor={
                          selectedButtonIndex !== null &&
                          modulePreviewData.elements.smallButtons[selectedButtonIndex]?.leds
                            ? modulePreviewData.elements.smallButtons[selectedButtonIndex].leds![2].color
                            : '#ffffff'
                        }
                        iconId={
                          selectedButtonIndex !== null &&
                          modulePreviewData.elements.smallButtons[selectedButtonIndex]?.leds
                            ? modulePreviewData.elements.smallButtons[selectedButtonIndex].leds![2].icon
                            : null
                        }
                      />
                      <ButtonsLedsEditors
                        ledId={4}
                        ledActive={
                          selectedButtonIndex !== null &&
                          modulePreviewData.elements.smallButtons[selectedButtonIndex]?.leds
                            ? modulePreviewData.elements.smallButtons[selectedButtonIndex].leds![3].active
                            : false
                        }
                        currentLedColor={
                          selectedButtonIndex !== null &&
                          modulePreviewData.elements.smallButtons[selectedButtonIndex]?.leds
                            ? modulePreviewData.elements.smallButtons[selectedButtonIndex].leds![3].color
                            : '#ffffff'
                        }
                        iconId={
                          selectedButtonIndex !== null &&
                          modulePreviewData.elements.smallButtons[selectedButtonIndex]?.leds
                            ? modulePreviewData.elements.smallButtons[selectedButtonIndex].leds![3].icon
                            : null
                        }
                      /> */}
                    </Stack>
                  )}

                  {/* Big Button Popover */}
                  {/* {selectedButtonIndex !== null && selectedButtonIndex === -1 && (
                    <Stack direction="column" spacing={1.3}>
                      <Stack justifyContent="space-between" direction="row">
                        <Typography
                          sx={{ color: theme.palette.primary.main }}
                          fontSize="1rem"
                          textAlign="start"
                          width="100%"
                          fontWeight="bold"
                        >
                          Linse {Object.keys(modulePreviewData.elements.symbols).length + 1}
                        </Typography>
                      </Stack>
                      <Stack direction="row" sx={{ height: '3.5rem' }} gap={1}>
                        <IconDisplayBig
                          label={t('symbol')}
                          isReadOnly={isReadOnly}
                          isCustomIcon={!!modulePreviewData.elements.bigButton?.useCustomIcon}
                          iconImage={
                            getIconImageForIconDisplayBig('bigButton')

                            // selectedButtonIndex !== null &&
                            // modulePreviewData.elements.bigButton.icon !== null
                            //   ? `${getMimeType(
                            //       iconMap[modulePreviewData.elements.bigButton.icon || 0]
                            //     )}${uint8ToBase64(
                            //       iconMap[modulePreviewData.elements.bigButton.icon || 0]
                            //     )}`
                            //   : null
                          }
                          onClick={() => {
                            setIconPickerOpen({
                              ledId: 0,
                              side: null,
                              showOnlyBargraphSymbols: false,
                            });
                          }}
                        />
                        <ColorDisplayBig
                          label={t('color')}
                          isReadOnly={isReadOnly}
                          color={selectedButtonIndex === null ? '#ffffff' : modulePreviewData.elements.bigButton.color}
                          onClick={() => {
                            setColorPickerOpen({ ledId: 0, side: 'middle' });
                          }}
                        />
                      </Stack>
                      <Stack direction="row" gap={1}>
                        <FormControlLabel
                          labelPlacement="start"
                          sx={{ m: 0, transform: 'translateX(0.2rem)' }}
                          control={
                            <Switch
                              size="small"
                              color="success"
                              disabled={isReadOnly}
                              checked={
                                modulePreviewData != null && selectedButtonIndex !== null
                                  ? modulePreviewData.elements.bigButton?.framedBorder
                                  : false
                              }
                              onChange={(e) => handleChangeButtonFrameBorder(e)}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: '0.65rem' }} variant="body1">
                              {t('frame_border')}
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          labelPlacement="start"
                          sx={{ m: 0, transform: 'translateX(0.45rem)' }}
                          control={
                            <Switch
                              size="small"
                              color="success"
                              disabled={isReadOnly}
                              checked={
                                modulePreviewData != null && selectedButtonIndex !== null
                                  ? modulePreviewData.elements.bigButton?.embossing
                                  : false
                              }
                              onChange={(e) => handleChangeButtonEmbossing(e)}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: '0.65rem' }} variant="body1">
                              {t('embossing')}
                            </Typography>
                          }
                        />
                      </Stack>

                      <Box sx={{ pb: '0.2rem', pt: '0.2rem' }}>
                        <Divider />
                      </Box>

                      <Stack
                        gap={1}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '17rem',
                          overflowX: 'auto',
                          padding: '1rem 1rem',
                        }}
                        ref={pickerRef}
                      >
                        {Array(Object.keys(modulePreviewData.elements.bigButton.leds).length)
                          .fill(null)
                          .map((_, index) => (
                            <ButtonsLedsEditors
                              key={index}
                              ledId={index + 1}
                              ledActive={
                                selectedButtonIndex !== null
                                  ? modulePreviewData.elements.bigButton.leds[index].active
                                  : false
                              }
                              currentLedColor={
                                selectedButtonIndex !== null
                                  ? modulePreviewData.elements.bigButton.leds[index].color
                                  : '#ffffff'
                              }
                              iconId={
                                selectedButtonIndex !== null
                                  ? modulePreviewData.elements.bigButton.leds[index].icon
                                  : null
                              }
                            />
                          ))}
                      </Stack>
                    </Stack>
                  )} */}
                </Box>
              </CustomPopover>
              {/* Hint Popover */}
              {/* <CustomPopover
                open={hintPopover.open}
                anchorEl={anchorEl}
                onClose={() => {}}
                hiddenArrow
                arrow="top-left"
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                sx={{
                  maxWidth: '17rem',
                  transform: isMobileScreen ? 'none' : 'translate(-2rem, -2rem) !important',
                  zIndex: 1000,
                  ...(isMobileScreen && { left: '15% !important' }),
                }}
                // anchorOrigin={{
                //   vertical: 'center',
                //   horizontal: 'right',
                // }}
                // transformOrigin={{
                //   vertical: 'center',
                //   horizontal: 70,
                // }}
              >
                <Stack gap={2} sx={{ p: '1rem' }}>
                  <Typography variant="body1">{t('hint_symbol_and_color')}</Typography>
                  <Button
                    sx={{ maxWidth: 'fit-content' }}
                    component="span"
                    variant="contained"
                    color="primary"
                    onClick={() => handleClickHintsUnderstoodButton()}
                  >
                    {t('understood_button')}
                  </Button>
                </Stack>
              </CustomPopover> */}

              {/* STEP 2 BODY CONTAINER */}
              <Grid className="Step-2-body-container" justifyContent="center" sx={{ height: '100%' }} container>
                {/* Display Selector Dialog */}
                <Menu
                  anchorEl={anchorEl}
                  open={displaySelectorOpen}
                  onClose={() => {
                    setAnchorEl(null);
                    setDisplaySelectorOpen(false);
                  }}
                >
                  {displayList.map((display, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        handleDisplayChange(display.id);
                        setAnchorEl(null);
                        setDisplaySelectorOpen(false);
                      }}
                    >
                      {display.name}
                    </MenuItem>
                  ))}
                </Menu>

                <Grid
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: { xs: 'none', lg: '50rem' },
                  }}
                  xs={12}
                  lg={9}
                  item
                >
                  <Stack direction="row" gap={3} sx={{ pb: '2rem' }}>
                    {/* <Stack direction="row" justifyContent="space-between">
                      <Box
                        className="pilot-miunske-logo"
                        sx={{
                          backgroundImage: `url(${process.env.REACT_APP_BASE_URL}assets/images/logos/pilot-miunske-logo.png)`,
                          width: '5rem',
                          height: '3rem',
                          marginLeft: isMobileScreen ? '0.5rem' : '0rem',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: 'contain',
                        }}
                      />
                    </Stack> */}
                    <Stack justifyContent="center" gap={0.1}>
                      <Stack direction="row">
                        <InterfaceContentTitle variant="h5">{steps[currentStep].label}</InterfaceContentTitle>
                        <span>&nbsp;</span>
                        {isReadOnly && (
                          <>
                            <span>&nbsp;</span>
                            <InterfaceContentTitle color="#919EAB" variant="h5">
                              ({t('read_only_mode')})
                            </InterfaceContentTitle>
                          </>
                        )}
                      </Stack>

                      {(editMode || autoSavedProgress) && (
                        <Box>
                          <InterfaceContentSubtitle pr={0.3} variant="h6">
                            {modulePreviewData.module}
                          </InterfaceContentSubtitle>
                          {projectTitle !== '' && (
                            <InterfaceContentSubtitle variant="h6">
                              <span>&nbsp;</span>
                              {' - '}&quot;{projectTitle}&quot;
                              <span>&nbsp;</span>
                            </InterfaceContentSubtitle>
                          )}

                          <InterfaceContentSubtitleGray variant="h6">
                            {' -  '}
                            Version {modulePreviewData.version}
                          </InterfaceContentSubtitleGray>
                        </Box>
                      )}
                      {!editMode && !autoSavedProgress && (
                        <Box sx={{ display: 'inline-flex' }}>
                          <InterfaceContentTitle variant="h5">
                            ({selectedModule?.type}) {selectedModule?.name != null && t(selectedModule?.name)}
                          </InterfaceContentTitle>
                          {projectTitle !== '' && (
                            <InterfaceContentTitle variant="h5">
                              <span>&nbsp;</span>
                              {' - '}&quot;{projectTitle}&quot;
                            </InterfaceContentTitle>
                          )}
                        </Box>
                      )}
                    </Stack>
                  </Stack>

                  <Box
                    className="Interface-Preview-Wrapper-Scroll"
                    onClick={() => {
                      setDissmissHints(true);
                    }}
                    sx={{
                      display: 'flex',
                      py: '1.5rem',
                      flex: '1',
                      height: '100%',
                      alignContent: 'center',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'column',
                      textAlign: 'left',
                    }}
                  >
                    {modulePreviewData !== null && (
                      <InterfacePreview
                        buttonRef={handleSetButtonRef}
                        id="interface-preview"
                        hintsActive={hintsActive}
                        handleClickHintDot={() => handleClickHintsUnderstoodButton()}
                        loadingIcons={loadingIcons}
                        arrayOfIcons={arrayOfIcons}
                        modulePreviewData={modulePreviewData}
                        handleElementClick={handleRockerSwitchLightClick}
                        handleDisplayClick={handleDisplayClick}
                        displayList={displayList}
                      />
                    )}
                  </Box>
                </Grid>

                <Grid
                  xs={12}
                  md={12}
                  lg={3}
                  sx={{
                    zIndex: 99,
                    alignContent: 'center',
                    marginTop: {
                      lg: '6.5rem',
                      xs: '1rem',
                    },
                  }}
                  item
                >
                  <CenteredBox className="Centered-Box">
                    <Paper
                      sx={{
                        height: '100%',
                        display: 'flex',
                        padding: '1rem',
                        boxShadow: '0 20px 40px -4px rgba(0,0,0,0.1)',
                      }}
                    >
                      <Box
                        className="Centered-Box-Inner-Container"
                        sx={{
                          display: 'flex',
                          minWidth: '10rem',
                          flexDirection:
                            // isRowLayout()
                            false ? 'row' : { xs: 'column', md: 'row', lg: 'column' },
                          alignItems: 'center',
                          justifyContent: 'space-around',
                          padding: '0.5rem',
                        }}
                        gap={2}
                      >
                        {isReadOnly && (
                          <>
                            <Grid item>
                              <Button
                                variant="contained"
                                color="primary"
                                sx={{ maxWidth: 'fit-content', mt: '0.4rem' }}
                                onClick={handleEnableEditMode}
                              >
                                {t('edit_configuration')}
                              </Button>

                              <Divider orientation="horizontal" flexItem />
                            </Grid>

                            {isSmallWebScreen && !isTabletScreen ? (
                              <Divider orientation="vertical" flexItem />
                            ) : (
                              <Divider orientation="horizontal" flexItem />
                            )}
                          </>
                        )}
                        <Grid
                          container
                          justifyContent="space-between"
                          width="100%"
                          gap={isSmallWebScreen && !isTabletScreen ? 2 : 1}
                        >
                          <Grid item lg={5.7}>
                            <FormControl sx={{ minWidth: '8.5rem' }} size="small">
                              <InputLabel id="verbau-selector">{t('verbau_label')}</InputLabel>
                              <Select
                                labelId="verbau-selector"
                                id="verbau-selector"
                                disabled={isReadOnly}
                                value={modulePreviewData != null ? modulePreviewData.rotation : '0'}
                                label={t('verbau_label')}
                                onChange={(e) => handleChangeOrientation(e.target.value as '0' | '90' | '180' | '270')}
                              >
                                <MenuItem value="0">{t('0°')}</MenuItem>
                                <MenuItem value="90">{t('90°')}</MenuItem>
                                <MenuItem value="180">{t('180°')}</MenuItem>
                                <MenuItem value="270">{t('270°')}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>
                    </Paper>
                  </CenteredBox>
                </Grid>
              </Grid>
            </InterfaceContentBox>
          )}

          {currentStep === 2 && (
            <InterfaceContentBox sx={{ overflowY: 'scroll', minHeight: 'fit-content' }} className="interface-content">
              <Grid height="100%" container>
                <Grid height="100%" item xs={12}>
                  <Stack direction="row" gap={3}>
                    {/* <Stack direction="row" justifyContent="space-between">
                      <Box
                        className="pilot-miunske-logo"
                        sx={{
                          backgroundImage: `url(${process.env.REACT_APP_BASE_URL}assets/images/logos/pilot-miunske-logo.png)`,
                          width: '5rem',
                          height: '3rem',
                          marginLeft: isMobileScreen ? '0.5rem' : '0rem',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: 'contain',
                        }}
                      />
                    </Stack> */}
                    <Stack justifyContent="center" gap={0.1}>
                      <Stack direction="row">
                        <InterfaceContentTitle variant="h5">{steps[currentStep].label}</InterfaceContentTitle>

                        {isReadOnly && (
                          <>
                            <span>&nbsp;</span>
                            <InterfaceContentTitle color="#919EAB" variant="h5">
                              ({t('read_only_mode')})
                            </InterfaceContentTitle>
                          </>
                        )}
                      </Stack>

                      {(editMode || autoSavedProgress) && (
                        <Box>
                          <InterfaceContentSubtitle pr={0.3} variant="h6">
                            {modulePreviewData.module}
                          </InterfaceContentSubtitle>
                          {projectTitle !== '' && (
                            <InterfaceContentSubtitle variant="h6">
                              <span>&nbsp;</span>
                              {' - '}&quot;{projectTitle}&quot;
                              <span>&nbsp;</span>
                            </InterfaceContentSubtitle>
                          )}
                          <InterfaceContentSubtitleGray variant="h6">
                            {' -  '}
                            Version {modulePreviewData.version}
                          </InterfaceContentSubtitleGray>
                        </Box>
                      )}
                      {!editMode && !autoSavedProgress && (
                        <InterfaceContentTitle variant="h5">
                          {selectedModule?.name != null && selectedModule?.name}
                        </InterfaceContentTitle>
                      )}
                    </Stack>
                  </Stack>
                  <CenteredBox className="Centered-Box">
                    <Stack
                      direction={isSmallWebScreen ? 'column-reverse' : 'row'}
                      alignItems={isSmallWebScreen ? 'center' : 'end'}
                      width="100%"
                    >
                      <Box
                        sx={{
                          width: '100%',
                          alignContent: 'end',
                          minWidth: '10rem',
                          flex: 1,
                          paddingTop: '1rem',
                        }}
                      >
                        <img
                          style={{
                            width: isMobileScreen ? '100%' : '18rem',
                          }}
                          alt=""
                          src="https://firebasestorage.googleapis.com/v0/b/miunskekonfigurator.appspot.com/o/other%2FDE-Finanziert_von_der_Europa%CC%88ischen_Union_WHITE.png?alt=media&token=8add0dbb-d7c3-4f84-b6c1-86a810242fde"
                        />
                      </Box>

                      {moduleSent === false ? (
                        <Stack
                          direction="row"
                          flexWrap={isMobileScreen ? 'wrap' : 'nowrap'}
                          maxWidth="46rem"
                          minWidth={isTabletScreen ? '100%' : '46rem'}
                          marginTop="1rem"
                          flex={10}
                          width="100%"
                          className="User-Info-Inputs-Stack"
                          gap={2}
                        >
                          <Stack width="100%" gap={2}>
                            <InterfaceContentTitle nouppercase variant="h6">
                              {t('step4_send_form_title')}
                            </InterfaceContentTitle>
                            <InputsGroupsContainer>
                              <TextField
                                id="name"
                                label={`${t('first_name')} *`}
                                name="name"
                                variant="outlined"
                                autoComplete="given-name"
                                value={userInfo.name}
                                disabled={sendingModule || modulePreviewData.isSubmitted}
                                error={!!errors.name}
                                helperText={errors.name || ''}
                                onChange={handleUserInfoChange}
                                sx={contactFormInputsStyle}
                              />

                              <TextField
                                id="lastname"
                                label={`${t('last_name')} *`}
                                name="lastname"
                                autoComplete="family-name"
                                value={userInfo.lastname}
                                disabled={sendingModule || modulePreviewData.isSubmitted}
                                error={!!errors.lastname}
                                helperText={errors.lastname || ''}
                                onChange={handleUserInfoChange}
                                sx={contactFormInputsStyle}
                              />
                            </InputsGroupsContainer>

                            <InputsGroupsContainer>
                              <TextField
                                fullWidth
                                id="postalCode"
                                label={`${t('postal_code')} *`}
                                name="postalCode"
                                autoComplete="postal-code"
                                value={userInfo.postalCode}
                                disabled={sendingModule || modulePreviewData.isSubmitted}
                                error={!!errors.postalCode}
                                helperText={errors.postalCode || ''}
                                onChange={handleUserInfoChange}
                                sx={contactFormInputsStyle}
                              />

                              <CountrySelect
                                id="country-select"
                                name="country"
                                value={userInfo.country}
                                disabled={modulePreviewData.isSubmitted || sendingModule}
                                error={!!errors.country}
                                helperText={errors.country || ''}
                                onChange={handleUserInfoChange}
                              />
                            </InputsGroupsContainer>

                            <InputsGroupsContainer>
                              <TextField
                                fullWidth
                                id="firma"
                                label={`${t('company')} *`}
                                name="firma"
                                autoComplete="organization"
                                value={userInfo.firma}
                                disabled={sendingModule || modulePreviewData.isSubmitted}
                                error={!!errors.firma}
                                helperText={errors.firma || ''}
                                onChange={handleUserInfoChange}
                                sx={contactFormInputsStyle}
                              />

                              <TextField
                                fullWidth
                                id="clientNumber"
                                label={`${t('customer_number')}`}
                                name="clientNumber"
                                value={userInfo.clientNumber || ''}
                                disabled={
                                  (editMode &&
                                    modulePreviewData.userInfo.clientNumber !== null &&
                                    modulePreviewData.isSubmitted) ||
                                  sendingModule ||
                                  isReadOnly
                                }
                                onChange={handleUserInfoChange}
                                sx={contactFormInputsStyle}
                              />
                            </InputsGroupsContainer>

                            <InputsGroupsContainer>
                              <TextField
                                fullWidth
                                id="email"
                                label={`${t('email')} *`}
                                name="email"
                                autoComplete="email"
                                value={userInfo.email}
                                disabled={sendingModule || modulePreviewData.isSubmitted}
                                error={!!errors.email}
                                helperText={errors.email || ''}
                                onChange={handleUserInfoChange}
                                sx={contactFormInputsStyle}
                              />

                              <TextField
                                fullWidth
                                id="telephoneNumber"
                                label={`${t('phone_number')}`}
                                name="telephoneNumber"
                                autoComplete="tel-national"
                                value={userInfo.telephoneNumber}
                                disabled={
                                  (editMode &&
                                    modulePreviewData.userInfo.telephoneNumber !== '' &&
                                    modulePreviewData.isSubmitted) ||
                                  sendingModule ||
                                  isReadOnly
                                }
                                onChange={handleUserInfoChange}
                                sx={contactFormInputsStyle}
                              />
                            </InputsGroupsContainer>

                            <TextField
                              fullWidth
                              multiline
                              maxRows={3.5}
                              label={`${t('user_comments')}`}
                              name="comments"
                              value={userInfo.comments}
                              disabled={sendingModule || isReadOnly}
                              onChange={handleUserInfoChange}
                              sx={{
                                minHeight: '8rem',
                                height: '100%',
                                '& .MuiInputBase-root': {
                                  height: '100%',
                                  backgroundColor: 'white',
                                  borderRadius: '0.3rem',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: '1px solid #777771',
                                },
                                '& textarea': {
                                  height: '100% !important',
                                },
                              }}
                            />
                          </Stack>
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '23rem',
                          }}
                        >
                          <Typography variant="h5" color="success">
                            {t('configuration_sent_success')}
                          </Typography>
                          {modulePreviewData.isSubmitted && (
                            <Button
                              variant="contained"
                              color="primary"
                              sx={{ marginTop: '2rem', width: '280px' }}
                              onClick={() => {
                                setModuleSent(false);
                                setSendingModule(false);
                                setCurrentStep(1);
                                setEditMode(true);
                              }}
                            >
                              {t('continue_editing')}
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            color="secondary"
                            sx={{ marginTop: '2rem', width: '280px' }}
                            onClick={() => {
                              resetConfigurator();
                            }}
                          >
                            {t('restart_configurator')}
                          </Button>
                        </Box>
                      )}
                      <Box
                        sx={{
                          visibility: 'hidden',
                          width: '100%',
                          minWidth: '20rem',
                          flex: 1,
                        }}
                      >
                        a
                      </Box>
                    </Stack>
                  </CenteredBox>
                </Grid>
              </Grid>
            </InterfaceContentBox>
          )}
        </ContentTransition>
        <ButtonContainer>
          <Button
            variant="contained"
            color="primary"
            sx={{
              visibility: determineBackButtonVisibility(),
            }}
            onClick={handleGoBackStep}
            disabled={sendingModule || moduleSent}
          >
            <BackCharacter>{'< '}</BackCharacter> {t('back_button')}
          </Button>
          {configurationUploadProgress === -1 ? (
            <Box
              sx={{
                padding: '0.5rem 0rem',
                visibility: isTabletScreen ? 'visible' : 'hidden',
              }}
            >
              <Typography>
                {currentStep + 1}/{steps.length}
              </Typography>
            </Box>
          ) : (
            <CircularProgressWithLabel value={configurationUploadProgress} t={t} />
          )}
          <Button
            sx={{ padding: '0.5rem 3.7rem' }}
            variant="contained"
            disabled={disableNextButton()}
            color="primary"
            onClick={() => {
              if (currentStep === 0) {
                handleOpenProjectTitleDialog();
                handleGoNextStep();
              } else {
                handleGoNextStep();
              }
            }}
          >
            {currentStep === 2 ? t('send_button') : t('next_button')}
            {sendingModule && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Button>
        </ButtonContainer>
      </ContentArea>
    </GlobalContainer>
  );
}
